import ContentShell from "components/ContentShell";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  FormLabel,
  IconButton,
  Typography,
  Divider,
  Paper,
  Chip,
  Alert,
  Tooltip,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import TuneIcon from "@mui/icons-material/Tune";
import LayersIcon from "@mui/icons-material/Layers";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TranslateIcon from "@mui/icons-material/Translate";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import useStyles from "styles/banner/bannerEditor";
import { SimpleTextInput } from "components/FormTextInput";
import ModalContainer from "components/ModalContainer";
import ModalLayout from "components/ModalLayout";
import CustomButton from "components/CustomButton";
import {
  createChallenge,
  getChallengeInfo,
  getChallengePrizeWheels,
  getConvertedRewardXP,
  updateChallenge,
} from "services/challengeService";
import { getChallengeRewardImages } from "services/challengeRewardImageService";
import FilePicker from "components/FilePicker";
import ImagePreviewBox from "components/ImagePreviewBox";
import * as Yup from "yup";
import {
  uploadImageV2,
  uploadContentTranslations,
} from "services/uploadService";
import { SimpleSelectInput } from "components/FormSelectInput";
import { SimpleTimeDateInput } from "components/FormTimeDateInput";
import TranslationButtons from "components/TranslationButtons";
import useOpenClose from "hooks/useOpenClose";
import ViewLanguageModal from "screens/modals/ViewLanguageModal";
import SimpleSwitch from "components/Switch";
import sessionHelperInstance from "services/sessionHelper";
import { getPlayerPacks, packInfo } from "services/packServices";
import { ImageType, DEFAULT_IMAGE_DIMENSIONS } from "utils/imageConstants";
import debounce from "lodash.debounce";
import { getUniquesArrayObject } from "utils/miscUtils";
import { defaultPageNo, defaultPageSize } from "utils/constants";
import SearchableInput from "components/SearchableInput";
import { getConfigurations, getStandardOddsKeys } from "services/miscService";
import { getObjectiveTypeTitles } from "services/objectiveTypeTitleService";
import BulkTranslationUpload from "components/BulkTranslationUpload";
import TerritoryXpVirtualList from "./TerritoryXpVirtualList";

// ─── Enum Options ────────────────────────────────────────────
const challengeTierOptions = [
  { id: "STANDARD", label: "STANDARD" },
  { id: "PREMIUM", label: "PREMIUM" },
  { id: "OTHER", label: "OTHER" },
];

const rewardTypeOptions = [
  { id: "PBF_COINS_SMALL", label: "PBF COINS (SMALL)" },
  { id: "PBF_COINS_MEDIUM", label: "PBF COINS (MEDIUM)" },
  { id: "PBF_COINS_LARGE", label: "PBF COINS (LARGE)" },
  { id: "XP", label: "XP" },
  { id: "PACK", label: "PACK" },
  { id: "MYSTERY_PLAYER_CARD", label: "MYSTERY PLAYER CARD" },
  { id: "XP_MULTIPLIER", label: "XP MULTIPLIER" },
  { id: "NEXT_VAULT_LEVEL", label: "NEXT VAULT LEVEL" },
  { id: "FREE_STORE_PACK", label: "FREE STORE PACK" },
  { id: "COIN_PRIZE_WHEEL", label: "COIN PRIZE WHEEL" },
];

const FIXED_QUANTITY_REWARD_TYPES = ["NEXT_VAULT_LEVEL", "FREE_STORE_PACK", "COIN_PRIZE_WHEEL"];

const REWARD_PACK_FILTER = { type: "Reward", isDisabled: false };

const needsRewardDuration = (rewardType: string) => rewardType === "XP_MULTIPLIER";

const decomposeDuration = (totalMinutes?: number | null) => {
  const total = Math.max(0, Math.floor(Number(totalMinutes) || 0));
  return {
    days: Math.floor(total / 1440),
    hours: Math.floor((total % 1440) / 60),
    minutes: total % 60,
  };
};

const composeDuration = (days: number, hours: number, minutes: number) =>
  (Number(days) || 0) * 1440 + (Number(hours) || 0) * 60 + (Number(minutes) || 0);

const needsPrizeWheel = (rewardType: string) => rewardType === "COIN_PRIZE_WHEEL";

const hasFixedRewardQuantity = (rewardType: string) => FIXED_QUANTITY_REWARD_TYPES.includes(rewardType);

const hasFixedObjectiveTarget = (objectiveType: string) => objectiveType === "CHANGE_TEAM_BADGE";

const scheduleTypeOptions = [
  { id: "ONCE", label: "ONCE" },
  { id: "DAILY", label: "DAILY" },
  { id: "WEEKLY", label: "WEEKLY" },
  { id: "MONTHLY", label: "MONTHLY" },
  { id: "CUSTOM", label: "CUSTOM" },
];

const positionGroupOptions = [
  { id: "Defenders", label: "DEFENDERS (LB, CB, RB)" },
  { id: "Midfielders", label: "MIDFIELDERS (CDM, CM, RM, LM)" },
  { id: "Attackers", label: "ATTACKERS (CAM, RW, LW, ST)" },
];

// Maps to ChallengeBetMarketType in game backend (13 values: 8 primary + 5 sub-markets)
const bettingMarketOptions = [
  { id: "FINAL_RESULT", label: "FINAL RESULT" },
  { id: "UNDER_OVER", label: "GOALS OVER / UNDER (ANY)" },
  { id: "UNDER", label: "GOALS UNDER" },
  { id: "OVER", label: "GOALS OVER" },
  { id: "BTTS", label: "BTTS (BOTH TEAMS TO SCORE)" },
  { id: "RESULT_BTTS", label: "RESULT / BTTS" },
  { id: "CORRECT_SCORE", label: "CORRECT SCORE" },
  {
    id: "FIRST_LAST_ANY_GOAL_SCORE",
    label: "FIRST / LAST / ANYTIME GOALSCORER (ANY)",
  },
  { id: "FIRST_GOAL_SCORE", label: "FIRST GOALSCORER" },
  { id: "LAST_GOAL_SCORE", label: "LAST GOALSCORER" },
  { id: "ANY_GOAL_SCORE", label: "ANYTIME GOALSCORER" },
  { id: "HANDICAPS", label: "HANDICAPS" },
];

// ─── Objective Type Hints ────────────────────────────────────
const objectiveTypeDescriptions: Record<
  string,
  { example: string; hint: string }
> = {
  GOALS_SCORED: {
    example: "Score N goals in any match",
    hint: "Counts every goal scored by any player in your team.",
  },
  GOALS_BY_POSITION: {
    example: "Score N goals by a Defender",
    hint: "Only goals scored by players in the selected position group count.",
  },
  GOALS_BY_NATIONALITY: {
    example: "Score N goals with English players",
    hint: "Only goals scored by players of the selected nationality count.",
  },
  GOALS_BY_CLUB: {
    example: "Score N goals with Arsenal players",
    hint: "Only goals scored by players from the selected club count.",
  },
  CONSECUTIVE_WINS: {
    example: "Win N matches in a row",
    hint: "The win streak resets if the player loses or draws.",
  },
  CLEAN_SHEETS: {
    example: "Keep N clean sheets",
    hint: "Counts matches where your team concedes 0 goals.",
  },
  CONSECUTIVE_CLEAN_SHEETS: {
    example: "Keep N clean sheets in a row",
    hint: "The streak resets if any goal is conceded.",
  },
  CONSECUTIVE_SCORING: {
    example: "Score in N consecutive matches",
    hint: "Player must score at least one goal in each consecutive match.",
  },
  WINS_WITH_NATIONALITY: {
    example: "Win N matches with ≥ 3 Brazilian players",
    hint: "Requires a minimum number of players from the chosen nationality in the lineup.",
  },
  WINS_WITH_CLUB: {
    example: "Win N matches with ≥ 3 Arsenal players",
    hint: "Requires a minimum number of players from the chosen club in the lineup.",
  },
  BETS_PLACED: {
    example: "Place N bets",
    hint: "Counts all settled bets regardless of betting market.",
  },
  BETS_PLACED_MARKET: {
    example: "Place N bets on Correct Score markets",
    hint: "Counts settled bets matching the selected betting market.",
  },
  BET_WIN_BY_ODDS: {
    example: "Win N bets with odds > 2.5",
    hint: "Only resolved winning bets whose decimal odds meet the minimum threshold count.",
  },
  CONSECUTIVE_BETS: {
    example: "Place bets in N consecutive matches",
    hint: "The streak resets if the player does not place any bet in a match.",
  },
  CONSECUTIVE_BETS_ON_MARKET: {
    example: "Place bets on Correct Score in N consecutive matches",
    hint: "The streak resets if the player does not place a bet on the selected market in a match.",
  },
  LEAGUE_POINTS: {
    example: "Accumulate N league points",
    hint: "Tracks total points earned in league matches.",
  },
  VAULT_LEVEL_UP: {
    example: "Level up your vault N times",
    hint: "Each vault level-up increments the counter by 1.",
  },
  GET_PROMOTED: {
    example: "Get promoted to a higher division {{text1}} time(s)",
    hint: "Awarded each time the player clears the last milestone of their current division and advances to the next higher division (e.g. Division 5 → Division 4). Use {{text1}} in the title template for the target count. Set Objective Target to 1 for a single promotion, or higher for a multi-promotion challenge.",
  },
  PLAY_DAYS_THIS_WEEK: {
    example: "Play PBF for 5 days this week",
    hint: "Counts the number of distinct calendar days (UTC) the player completes at least one match during the current weekly cycle. Multiple matches on the same day count as 1 day.",
  },
  LEAGUE_MILESTONES: {
    example: "Reach N milestones on the Divisions ladder",
    hint: "Counts every milestone the player clears, in any division. The division number is irrelevant, and clearing Division 1 repeatedly keeps counting.",
  },
  CONSECUTIVE_BET_DAYS: {
    example: "Place a bet N days in a row",
    hint: "Counts consecutive calendar days (UTC) on which the player placed at least one bet. Skipping a day restarts the streak at 1.",
  },
  BET_DAYS_THIS_WEEK: {
    example: "Place a bet on N days this week",
    hint: "Counts distinct calendar days (UTC) with at least one bet during the current cycle. Several bets on the same day count as 1 day. Pair with a WEEKLY schedule.",
  },
  MATCHES_PLAYED: {
    example: "Play N matches",
    hint: "Counts every completed match regardless of result. No time restriction beyond the challenge window.",
  },
  BETS_ON_SINGLE_MATCH: {
    example: "Place N bets on a single match",
    hint: "All N bets must land on the SAME match. It is all-or-nothing per match: it completes only when one match carries at least Objective Target bets — placing fewer records no progress. Different markets on one match each count as a separate bet.",
  },
  STAKE_ON_MATCHES: {
    example: "Stake more than 20 on 10 matches",
    hint: "Objective Target is the number of matches; a match counts only when its total stake is strictly MORE than Minimum Stake (staking exactly that amount does not qualify). The stake is the total across all bets on that match. Note the stake is in the player's own currency and is not converted.",
  },
  STAKE_IN_TIME_PERIOD: {
    example: "Stake 20 before the challenge ends",
    hint: "Objective Target is the total amount to stake before the challenge expires — the challenge's own duration is the time limit, so there is no separate time to set. Each match's stake accumulates until the target is reached. The amount is in the player's own currency and is not converted.",
  },
  CHANGE_TEAM_BADGE: {
    example: "Change your team badge",
    hint: "Completes the first time the player saves a different team badge. Re-saving the badge they already have does not count. There is no target to set — it is fixed at 1.",
  },
  GOALS_IN_SINGLE_MATCH: {
    example: "Score more than N goals in a single match",
    hint: "Completes only when the player scores MORE than Objective Target goals in ONE match (target 5 ⇒ needs 6). It is all-or-nothing per match: falling short does not move the bar or create any progress. Use GOALS_SCORED for goals accumulated across matches.",
  },
};

// ─── Types ────────────────────────────────────────────────────
interface ObjectiveFilters {
  position?: string;
  nationality?: string;
  clubId?: string;
  minOdds?: number | null;
  minPlayers?: number | null;
  betType?: string;
  minStake?: number | null;
}

interface SubChallenge {
  _id?: string;
  tier: string;
  objectiveType: string;
  objectiveTarget: number | null;
  objectiveFilters: ObjectiveFilters;
  rewardType: string;
  rewardValue: number | null;
  rewardPacks: string[];
  rewardImageUrl?: string;
  rewardDurationMinutes?: number | null;
  prizeWheelConfigId?: string;
  currencyRewardValues?: Record<string, number | string>;
}

interface ChallengeDetails {
  type: string;
  cmsTitle: string;
  tier: string;
  objectiveType: string;
  objectiveTarget: number | null;
  objectiveFilters: ObjectiveFilters;
  rewardType: string;
  rewardValue: number | null;
  rewardPacks: string[];
  rewardImageUrl?: string;
  rewardDurationMinutes?: number | null;
  prizeWheelConfigId?: string;
  comboTitle: string;
  comboTitleTrans: Record<string, any>;
  comboRewardTitle: string;
  comboRewardTitleTrans: Record<string, any>;
  startDate: string;
  endDate: string;
  scheduleType: string;
  repeatEveryDays: number | null;
  operators: any[];
  excludedOperators: any[];
  territories: string[];
  sortOrder: number | null;
  isActive: boolean;
  isFtueChallenge: boolean;
  currencyRewardValues?: Record<string, number | string>;
  subChallenges: SubChallenge[];
}

const _emptySubChallenge: SubChallenge = {
  tier: "",
  objectiveType: "",
  objectiveTarget: null,
  objectiveFilters: {},
  rewardType: "",
  rewardValue: null,
  rewardPacks: [],
  rewardDurationMinutes: null,
  prizeWheelConfigId: "",
  currencyRewardValues: {},
};

const _challengeDetails: ChallengeDetails = {
  type: "INDIVIDUAL",
  cmsTitle: "",
  tier: "STANDARD",
  objectiveType: "",
  objectiveTarget: null,
  objectiveFilters: {},
  rewardType: "",
  rewardValue: null,
  rewardPacks: [],
  rewardDurationMinutes: null,
  prizeWheelConfigId: "",
  comboTitle: "",
  comboTitleTrans: {},
  comboRewardTitle: "",
  comboRewardTitleTrans: {},
  startDate: "",
  endDate: "",
  scheduleType: "ONCE",
  repeatEveryDays: null,
  operators: [],
  excludedOperators: [],
  territories: [],
  sortOrder: null,
  isActive: true,
  isFtueChallenge: false,
  currencyRewardValues: {},
  subChallenges: [],
};

// ─── Helper: is pack-based reward ──────────────────────
const isPackRewardType = (rt?: string) =>
  ["PACK", "MYSTERY_PLAYER_CARD"].includes(rt ?? "");

interface PackOption {
  id: string;
  label: string;
  imageUrl?: string | null;
}

const toPackOption = (pack: any): PackOption => ({
  id: pack._id,
  label: pack.name,
  imageUrl: pack.imageUrl ?? null,
});

interface RewardPreview {
  url: string;
  badge: "Custom" | "Pack" | "Default";
  caption: string;
}

const REWARD_BADGE_COLOR = {
  Custom: "primary",
  Pack: "success",
  Default: "default",
} as const;

const resolveRewardPreview = (
  rewardType: string | undefined,
  rewardImageUrl: string | undefined,
  packSelection: (PackOption | null)[] | undefined,
  rewardImageMap: Record<string, string | null>,
): RewardPreview | null => {
  if (rewardImageUrl) {
    return {
      url: rewardImageUrl,
      badge: "Custom",
      caption: "Uploaded image — used ahead of every fallback",
    };
  }

  const packImageUrl = isPackRewardType(rewardType)
    ? packSelection?.[0]?.imageUrl
    : null;
  if (packImageUrl) {
    return {
      url: packImageUrl,
      badge: "Pack",
      caption: "The selected pack's own image",
    };
  }

  const defaultUrl = rewardType ? rewardImageMap[rewardType] : null;
  if (defaultUrl) {
    return {
      url: defaultUrl,
      badge: "Default",
      caption:
        "Default image for this reward type — applied automatically if you don't upload one",
    };
  }

  return null;
};

const renderRewardPreview = (
  preview: RewardPreview | null,
  rewardType: string | undefined,
  alt: string,
  height: number,
  maxWidth: number,
) =>
  preview ? (
    <Box sx={{ mt: 1, maxWidth }}>
      <ImagePreviewBox
        url={preview.url}
        alt={alt}
        height={height}
        badge={
          <Chip
            size="small"
            color={REWARD_BADGE_COLOR[preview.badge]}
            label={preview.badge}
          />
        }
      />
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 0.5 }}
      >
        {preview.caption}
      </Typography>
    </Box>
  ) : (
    <Alert severity="warning" sx={{ mt: 1 }}>
      No image is available for this reward. Upload a custom image above,
      {isPackRewardType(rewardType)
        ? " select a reward pack that has its own image,"
        : ""}{" "}
      or set a default under Challenges → Challenge Reward Images.
    </Alert>
  );

// Deterministic S3 keys reuse the same URL after a default is replaced; append a per-load
// cache-buster so the editor shows the latest image instead of the browser-cached one.
const withCacheBuster = (url?: string): string | undefined =>
  url ? `${url.split("?")[0]}?t=${Date.now()}` : undefined;

// ─── Validation ─────────────────────────────────────────────
const challengeValidation = Yup.object().shape({
  type: Yup.string()
    .oneOf(["INDIVIDUAL", "COMBO"])
    .required("Type is required"),
  tier: Yup.string()
    .nullable()
    .when("type", {
      is: "INDIVIDUAL",
      then: (schema) =>
        schema.required("Tier is required for Individual challenges"),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
  startDate: Yup.string().required("Start date is required"),
  endDate: Yup.string().required("End date is required"),
  scheduleType: Yup.string().required("Schedule type is required"),
  repeatEveryDays: Yup.number()
    .nullable()
    .when("scheduleType", {
      is: "CUSTOM",
      then: (schema) =>
        schema
          .required("Cycle interval is required for Custom schedule")
          .min(1, "Cycle interval must be at least 1 day"),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
  rewardType: Yup.string().required("Reward type is required"),
  rewardValue: Yup.number()
    .required("Reward value is required")
    .positive("Reward value must be positive"),
  objectiveType: Yup.string()
    .nullable()
    .when("type", {
      is: "INDIVIDUAL",
      then: (schema) => schema.required("Objective type is required"),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
  objectiveTarget: Yup.number()
    .nullable()
    .when(["type", "objectiveType"], {
      is: (type: string, objectiveType: string) =>
        type === "INDIVIDUAL" && !hasFixedObjectiveTarget(objectiveType),
      then: (schema) =>
        schema
          .required("Objective target is required")
          .positive("Target must be positive"),
      otherwise: (schema) => schema.notRequired(),
    }),
});

// ─── Helpers ────────────────────────────────────────────────
const needsPositionFilter = (objectiveType: string) =>
  objectiveType === "GOALS_BY_POSITION";

const needsNationalityFilter = (objectiveType: string) =>
  ["GOALS_BY_NATIONALITY", "WINS_WITH_NATIONALITY"].includes(objectiveType);

const needsClubFilter = (objectiveType: string) =>
  ["GOALS_BY_CLUB", "WINS_WITH_CLUB"].includes(objectiveType);

const needsMinOdds = (objectiveType: string) =>
  objectiveType === "BET_WIN_BY_ODDS";

const getNearbyOddsExamples = (value: number, keysSet: Set<string>): string => {
  const sorted = Array.from(keysSet)
    .map(Number)
    .sort((a, b) => a - b);
  const insertIdx = sorted.findIndex((k) => k > value);
  const idx = insertIdx === -1 ? sorted.length : insertIdx;
  const before = sorted.slice(Math.max(0, idx - 2), idx);
  const after = sorted.slice(idx, Math.min(sorted.length, idx + 3));
  return [...before, ...after].join(", ");
};

const needsMinPlayers = (objectiveType: string) =>
  ["WINS_WITH_NATIONALITY", "WINS_WITH_CLUB"].includes(objectiveType);

const needsBetTypeFilter = (objectiveType: string) =>
  ["BETS_PLACED_MARKET", "CONSECUTIVE_BETS_ON_MARKET"].includes(objectiveType);

const needsMinStake = (objectiveType: string) => objectiveType === "STAKE_ON_MATCHES";

const MAX_GOALS_PER_MATCH = 6;

const replaceTemplatePlaceholders = (template: string, values: string[]) => {
  if (!template) return "";
  return values.reduce((acc, value, idx) => {
    const token = new RegExp(`\\{\\{text${idx + 1}\\}\\}`, "g");
    return acc.replace(token, value);
  }, template);
};

const extractFilterValue = (value: any): any => {
  if (Array.isArray(value)) return extractFilterValue(value[0]);
  if (value && typeof value === "object") {
    return (
      value.id ||
      value._id ||
      value.value ||
      value.code ||
      value.iso2 ||
      value.label ||
      ""
    );
  }
  return value;
};

const toNullableNumber = (value: any): number | null =>
  value === null || value === undefined || value === "" ? null : Number(value);

const normalizeObjectiveFilters = (filters: any): ObjectiveFilters => {
  const safeFilters = filters || {};
  const minOddsRaw = extractFilterValue(safeFilters.minOdds);
  const minPlayersRaw = extractFilterValue(safeFilters.minPlayers);

  return {
    minStake: toNullableNumber(extractFilterValue(safeFilters.minStake)),
    position: extractFilterValue(safeFilters.position) || undefined,
    nationality: extractFilterValue(safeFilters.nationality) || undefined,
    clubId: extractFilterValue(safeFilters.clubId) || undefined,
    betType: extractFilterValue(safeFilters.betType) || undefined,
    minOdds:
      minOddsRaw === null || minOddsRaw === undefined || minOddsRaw === ""
        ? null
        : Number(minOddsRaw),
    minPlayers:
      minPlayersRaw === null ||
      minPlayersRaw === undefined ||
      minPlayersRaw === ""
        ? null
        : Number(minPlayersRaw),
  };
};

// ─── Objective Filter Validation ────────────────────────────
/**
 * Throws an Error if any required filter field for the given objectiveType is missing.
 * @param objectiveType  The objective type string
 * @param filters        The current ObjectiveFilters for that objective
 * @param prefix         Label prefix used in the error message, e.g. "" or "Sub-challenge #2: "
 */
const validateObjectiveFilters = (
  objectiveType: string,
  filters: ObjectiveFilters | null | undefined,
  standardOddsKeys: Set<string>,
  prefix = "",
) => {
  const f = filters || {};

  if (needsPositionFilter(objectiveType) && !f.position) {
    throw new Error(
      `${prefix}Position Group is required for "${objectiveType.replace(/_/g, " ")}"`,
    );
  }

  if (needsNationalityFilter(objectiveType) && !f.nationality) {
    throw new Error(
      `${prefix}Nationality is required for "${objectiveType.replace(/_/g, " ")}"`,
    );
  }

  if (needsClubFilter(objectiveType) && !f.clubId) {
    throw new Error(
      `${prefix}Club is required for "${objectiveType.replace(/_/g, " ")}"`,
    );
  }

  if (
    needsMinPlayers(objectiveType) &&
    (f.minPlayers === null ||
      f.minPlayers === undefined ||
      f.minPlayers === ("" as any))
  ) {
    throw new Error(
      `${prefix}Min Players is required for "${objectiveType.replace(/_/g, " ")}"`,
    );
  }

  if (
    needsMinOdds(objectiveType) &&
    (f.minOdds === null || f.minOdds === undefined || f.minOdds === ("" as any))
  ) {
    throw new Error(
      `${prefix}Minimum Odds is required for "${objectiveType.replace(/_/g, " ")}"`,
    );
  }

  if (
    needsMinOdds(objectiveType) &&
    f.minOdds != null &&
    f.minOdds !== ("" as any) &&
    standardOddsKeys.size > 0 &&
    !standardOddsKeys.has(String(f.minOdds))
  ) {
    throw new Error(
      `${prefix}Minimum Odds value ${f.minOdds} is not a valid standard odds value. Please enter a value that exists in the standard odds table.`,
    );
  }

  if (needsBetTypeFilter(objectiveType) && !f.betType) {
    throw new Error(
      `${prefix}Betting Market Type is required for "${objectiveType.replace(/_/g, " ")}"`,
    );
  }

  if (needsMinStake(objectiveType) && !(Number(f.minStake) > 0)) {
    throw new Error(
      `${prefix}Minimum Stake is required for "${objectiveType.replace(/_/g, " ")}" and must be above 0`,
    );
  }
};

const validateRewardConfig = (
  reward: {
    rewardType: string;
    rewardValue: number | null;
    rewardDurationMinutes?: number | null;
    prizeWheelConfigId?: string;
  },
  prefix = "",
) => {
  if (reward.rewardType === "XP_MULTIPLIER") {
    const multiplier = Number(reward.rewardValue);
    if (!Number.isInteger(multiplier) || multiplier < 2) {
      throw new Error(
        `${prefix}XP multiplier must be a whole number of 2 or more (e.g. 3 for 3x XP)`,
      );
    }
    const duration = Number(reward.rewardDurationMinutes);
    if (!Number.isInteger(duration) || duration < 1) {
      throw new Error(
        `${prefix}Multiplier duration is required — set at least 1 minute across Days / Hours / Minutes`,
      );
    }
  }

  if (needsPrizeWheel(reward.rewardType) && !reward.prizeWheelConfigId) {
    throw new Error(`${prefix}A prize wheel must be selected for the Coin Prize Wheel reward`);
  }

  if (hasFixedRewardQuantity(reward.rewardType) && Number(reward.rewardValue) !== 1) {
    throw new Error(
      `${prefix}Reward value must be 1 for "${reward.rewardType.replace(/_/g, " ")}"`,
    );
  }
};

// ─── Visual Data Constants ──────────────────────────────────
const rewardTypeInfo: Record<
  string,
  { icon: string; color: string; desc: string }
> = {
  PBF_COINS_SMALL: {
    icon: "🪙",
    color: "#ffa726",
    desc: "A small coin reward for quick wins",
  },
  PBF_COINS_MEDIUM: {
    icon: "💰",
    color: "#ff9800",
    desc: "A medium coin reward for mid-tier challenges",
  },
  PBF_COINS_LARGE: {
    icon: "💎",
    color: "#ef6c00",
    desc: "A large coin reward for major achievements",
  },
  XP: {
    icon: "⚡",
    color: "#7c4dff",
    desc: "Experience points to level up faster",
  },
  PACK: {
    icon: "📦",
    color: "#2e7d32",
    desc: "One or more player card packs",
  },
  MYSTERY_PLAYER_CARD: {
    icon: "🎴",
    color: "#6a1b9a",
    desc: "A random mystery player card",
  },
  XP_MULTIPLIER: {
    icon: "✨",
    color: "#6d28d9",
    desc: "Multiplies XP earned from gameplay for a set time — nothing is granted on claim",
  },
  NEXT_VAULT_LEVEL: {
    icon: "🚀",
    color: "#0891b2",
    desc: "Instantly tops the vault meter up to the next level, spin included",
  },
  FREE_STORE_PACK: {
    icon: "🎁",
    color: "#16a34a",
    desc: "The player's next store pack costs 0 coins — any pack they choose",
  },
  COIN_PRIZE_WHEEL: {
    icon: "🎡",
    color: "#db2777",
    desc: "Spins a CMS-configured PBF coin prize wheel; the slice is drawn server-side",
  },
};

const tierVisuals: Record<
  string,
  { bg: string; border: string; text: string; icon: string }
> = {
  STANDARD: {
    bg: "#e3f2fd",
    border: "#1976d2",
    text: "#1565c0",
    icon: "⭐",
  },
  PREMIUM: {
    bg: "#fff8e1",
    border: "#f9a825",
    text: "#e65100",
    icon: "👑",
  },
  OTHER: {
    bg: "#f3e5f5",
    border: "#9c27b0",
    text: "#7b1fa2",
    icon: "🔮",
  },
};

const scheduleInfo: Record<string, { icon: string; desc: string }> = {
  ONCE: {
    icon: "🎯",
    desc: "Challenge runs once between start and end date",
  },
  DAILY: {
    icon: "📅",
    desc: "Resets every day — each daily cycle is tracked separately",
  },
  WEEKLY: {
    icon: "📆",
    desc: "Resets every week — each weekly cycle is tracked separately",
  },
  MONTHLY: {
    icon: "🗓️",
    desc: "Resets every month — each monthly cycle is tracked separately",
  },
  CUSTOM: {
    icon: "⚙️",
    desc: "Resets on a custom interval — you define the cycle length in days",
  },
};

const objectiveCategoryColor: Record<string, string> = {
  GOALS_SCORED: "#2e7d32",
  GOALS_BY_POSITION: "#2e7d32",
  GOALS_BY_NATIONALITY: "#2e7d32",
  GOALS_BY_CLUB: "#2e7d32",
  CONSECUTIVE_WINS: "#1565c0",
  CLEAN_SHEETS: "#00838f",
  CONSECUTIVE_CLEAN_SHEETS: "#00838f",
  CONSECUTIVE_SCORING: "#6a1b9a",
  WINS_WITH_NATIONALITY: "#1565c0",
  WINS_WITH_CLUB: "#1565c0",
  BETS_PLACED: "#e65100",
  BETS_PLACED_MARKET: "#e65100",
  BET_WIN_BY_ODDS: "#e65100",
  LEAGUE_POINTS: "#37474f",
  VAULT_LEVEL_UP: "#f57c00",
  GET_PROMOTED: "#1565c0",
  PLAY_DAYS_THIS_WEEK: "#00695c",
  LEAGUE_MILESTONES: "#37474f",
  CONSECUTIVE_BET_DAYS: "#e65100",
  BET_DAYS_THIS_WEEK: "#00695c",
  MATCHES_PLAYED: "#455a64",
  BETS_ON_SINGLE_MATCH: "#e65100",
  STAKE_ON_MATCHES: "#e65100",
  STAKE_IN_TIME_PERIOD: "#e65100",
  CHANGE_TEAM_BADGE: "#7b1fa2",
  GOALS_IN_SINGLE_MATCH: "#2e7d32",
};

const getProgressSteps = (d: ChallengeDetails) => [
  { label: "Type", done: !!d.type },
  ...(d.type === "COMBO"
    ? [{ label: "Combo Title", done: !!d.comboTitle }]
    : []),
  {
    label: "Objective",
    done:
      d.type === "COMBO"
        ? d.subChallenges.length > 0 &&
          d.subChallenges.every(
            (s) => !!s.objectiveType && (s.objectiveTarget ?? 0) > 0,
          )
        : !!d.objectiveType && (d.objectiveTarget ?? 0) > 0,
  },
  { label: "Reward", done: !!d.rewardType && (d.rewardValue ?? 0) > 0 },
  {
    label: "Schedule",
    done: !!d.startDate && !!d.endDate && !!d.scheduleType,
  },
];

// Visual + interaction lock applied to fields that cannot be edited on a live challenge
const lockedSx = {
  opacity: 0.55,
  pointerEvents: "none" as const,
  userSelect: "none" as const,
};

// ─── Section Card ───────────────────────────────────────────
const SectionCard = ({
  step,
  icon,
  title,
  subtitle,
  accent = "#1976d2",
  children,
  isComplete,
  locked = false,
}: {
  step: number;
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  accent?: string;
  children: React.ReactNode;
  isComplete?: boolean;
  locked?: boolean;
}) => (
  <Paper
    elevation={0}
    sx={{
      border: "1px solid",
      borderColor: isComplete ? `${accent}60` : "divider",
      borderRadius: 3,
      mb: 3,
      overflow: "hidden",
      transition: "border-color 0.3s, box-shadow 0.3s",
      "&:hover": { boxShadow: `0 2px 16px ${accent}12` },
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2.5,
        py: 2,
        borderBottom: "1px solid",
        borderBottomColor: "divider",
        borderLeft: `4px solid ${accent}`,
        background: `linear-gradient(135deg, ${accent}0a 0%, transparent 100%)`,
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          background: `linear-gradient(135deg, ${accent}, ${accent}cc)`,
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13,
          fontWeight: 700,
          flexShrink: 0,
          boxShadow: `0 2px 8px ${accent}40`,
        }}
      >
        {step}
      </Box>
      <Box sx={{ color: accent, display: "flex", fontSize: 20 }}>{icon}</Box>
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="subtitle1"
          fontWeight={700}
          color="text.primary"
          sx={{ lineHeight: 1.3 }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
      {locked && (
        <Tooltip title="Locked while the challenge is live">
          <Chip
            label="🔒 Locked"
            size="small"
            sx={{
              fontSize: 11,
              fontWeight: 600,
              height: 22,
              bgcolor: "#eceff1",
              color: "#546e7a",
              border: "1px solid #cfd8dc",
            }}
          />
        </Tooltip>
      )}
      {isComplete !== undefined && (
        <Tooltip
          title={isComplete ? "Section complete" : "Fill in required fields"}
        >
          <Box
            sx={{
              display: "flex",
              color: isComplete ? "#4caf50" : "#bdbdbd",
            }}
          >
            {isComplete ? (
              <CheckCircleIcon fontSize="small" />
            ) : (
              <RadioButtonUncheckedIcon fontSize="small" />
            )}
          </Box>
        </Tooltip>
      )}
    </Box>
    <Box sx={{ p: 3, ...(locked ? lockedSx : {}) }}>{children}</Box>
  </Paper>
);

// ─── Component ───────────────────────────────────────────────
const ChallengesEditor = () => {
  const styles = useStyles();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const id = location.pathname.split("/").slice(-1)[0];
  const isEdit = id !== "create";
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [challengeDetails, setChallengeDetails] = useState<ChallengeDetails>(
    () => {
      if (isEdit) return _challengeDetails;
      // Default to a standard midnight-to-midnight window: tomorrow 00:00:00 to the
      // day after 00:00:00 (local time), so most challenges need no manual adjustment.
      const now = new Date();
      const start = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() + 1,
      );
      const end = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() + 2,
      );
      return {
        ..._challengeDetails,
        isActive: false,
        startDate: start.toISOString(),
        endDate: end.toISOString(),
      };
    },
  );
  const [rewardImage, setRewardImage] = useState<any | null>(null);
  const [subRewardImages, setSubRewardImages] = useState<(any | null)[]>([]);
  // Image dimension states — powers the "Image Dimensions" control in FilePicker
  const [rewardImageDimensions, setRewardImageDimensions] = useState<
    any | null
  >(null);
  const [subRewardImageDimensions, setSubRewardImageDimensions] = useState<
    (any | null)[]
  >([]);
  const [packOptions, setPackOptions] = useState<PackOption[]>([]);
  const [packSelections, setPackSelections] = useState<(PackOption | null)[]>(
    [],
  );
  const [subPackSelections, setSubPackSelections] = useState<
    (PackOption | null)[][]
  >([]);
  const [translationFile, setTranslationFile] = useState<File | null>(null);
  const { isOpen, close, open } = useOpenClose();
  const {
    isOpen: isTerritoryXpModalOpen,
    close: closeTerritoryXpModal,
    open: openTerritoryXpModal,
  } = useOpenClose();

  const [territoryXpValues, setTerritoryXpValues] = useState<
    Record<string, number | string>
  >({});
const [subChallengeMetaData, setSubChallengeMetaData] = useState<{
  isSubChallenge?: boolean;
  subChallengeIndex?: number;
}>({});
  const [currentTerritoryXpModalValues, setCurrentTerritoryXpModalValues] =
    useState<Record<string, number | string>>({});
  const [isTerritoryXpLoading, setIsTerritoryXpLoading] = useState(false);
  const [objectiveTypeOptions, setObjectiveTypeOptions] = useState<
    { id: string; label: string }[]
  >([]);
  const [objectiveTypeTitleMap, setObjectiveTypeTitleMap] = useState<
    Record<string, string>
  >({});
  const [standardOddsKeys, setStandardOddsKeys] = useState<Set<string>>(
    new Set(),
  );
  const [territoryXpErrors, setTerritoryXpErrors] = useState<
    Record<string, string>
  >({});
  // Default reward image per reward type — used to preview the auto-assigned
  // image and to warn when a reward type has no default configured.
  const [rewardImageMap, setRewardImageMap] = useState<
    Record<string, string | null>
  >({});
  const [prizeWheelOptions, setPrizeWheelOptions] = useState<
    { id: string; label: string }[]
  >([]);
  const [modalData, setModalData] = useState({
    language: "",
    inputValue: "",
    field: null as string | null,
    viewData: [] as any[],
    modal: "" as string,
    subChallengeIndex: null as number | null,
  });

  const cancel = () => navigate(configData.routes.challenges.challenges);

  const isNaturalNum = (value: any): boolean => {
    return Number.isInteger(value) && value > 0;
  };

  const fetchTerritoryXpValues = useCallback(
    async (
      rewardType: string,
      rewardValue: any,
      isSubChallenge?: boolean,
      subChallengeIndex?: number,
    ) => {
      if (rewardType !== "XP") {
        setCurrentTerritoryXpModalValues({});
        if (!isSubChallenge) {
          setChallengeDetails((prev) => ({
            ...prev,
            currencyRewardValues: {},
          }));
          return;
        } else {
          setChallengeDetails((prev) => ({
            ...prev,
            subChallenges: prev.subChallenges.map((sub, idx) =>
              idx === subChallengeIndex
                ? { ...sub, currencyRewardValues: {} }
                : sub,
            ),
          }));
          return;
        }
      }

      const parsedValue = Number(rewardValue);
      if (!isNaturalNum(parsedValue)) {
        showSnackbar(
          "Please enter a valid positive reward value to fetch territory XP values.",
          "error",
        );
        setCurrentTerritoryXpModalValues({});
        if (!isSubChallenge) {
          setChallengeDetails((prev) => ({
            ...prev,
            currencyRewardValues: {},
          }));
          return;
        } else {
          setChallengeDetails((prev) => ({
            ...prev,
            subChallenges: prev.subChallenges.map((sub, idx) =>
              idx === subChallengeIndex
                ? { ...sub, currencyRewardValues: {} }
                : sub,
            ),
          }));
          return;
        }
      }

      try {
        setTerritoryXpErrors({});
        setIsTerritoryXpLoading(true);
        const resp = await getConvertedRewardXP(parsedValue);

        const mappedValues: Record<string, number | string> = {};
        Object.entries(resp?.data?.currencyRewardValues || {})
          .sort(([a], [b]) => a.localeCompare(b))
          .forEach(([code, value]) => {
            const normalizedValue =
              typeof value === "number" || typeof value === "string"
                ? value
                : "";
            mappedValues[code] = normalizedValue;
          });

        setCurrentTerritoryXpModalValues(mappedValues);
        if (!isSubChallenge) {
          setChallengeDetails((prev) => ({
            ...prev,
            currencyRewardValues: mappedValues,
          }));
        } else {
          setChallengeDetails((prev) => ({
            ...prev,
            subChallenges: prev.subChallenges.map((sub, idx) =>
              idx === subChallengeIndex
                ? { ...sub, currencyRewardValues: mappedValues }
                : sub,
            ),
          }));
        }
      } catch (error: any) {
        showSnackbar(
          error?.message || "Failed to load territory XP values.",
          "error",
        );
      } finally {
        setIsTerritoryXpLoading(false);
      }
    },
    [showSnackbar],
  );

  const debouncedFetchTerritoryXpValues = useMemo(
  () =>
    debounce(
      (
        rewardType: string,
        rewardValue: any,
        isSubChallenge?: boolean,
        subChallengeIndex?: number,
      ) => {
        fetchTerritoryXpValues(
          rewardType,
          rewardValue,
          isSubChallenge,
          subChallengeIndex,
        );
      },
      300 // delay in ms
    ),
  []
);

useEffect(() => {
  return () => {
    debouncedFetchTerritoryXpValues.cancel();
  };
}, [debouncedFetchTerritoryXpValues]);

  const handleOpenTerritoryXpModal = async (
    values: SubChallenge,
    isSubChallenge: boolean,
    index?: number,
  ) => {
    if (!isSubChallenge) {
      setSubChallengeMetaData({ isSubChallenge: false, subChallengeIndex: null });
      setCurrentTerritoryXpModalValues(values.currencyRewardValues || {});
    } else {
      const subChallenge = challengeDetails.subChallenges[index || 0];
      setSubChallengeMetaData({ isSubChallenge: true, subChallengeIndex: index });
      setCurrentTerritoryXpModalValues(subChallenge.currencyRewardValues || {});
    }
    openTerritoryXpModal();
  };

  const handleTerritoryXpValueChange = (code: string, value: string) => {
    setCurrentTerritoryXpModalValues((prev) => ({
      ...prev,
      [code]: Number(value),
    }));

    let error = "";

    if (
      value === "" ||
      value === null ||
      value === undefined ||
      Number(value) <= 0 ||
      isNaN(Number(value))
    ) {
      error = `${code} territory must have a positive value.`;
    }

    setTerritoryXpErrors((prev) => ({
      ...prev,
      [code]: error,
    }));
  };

  const validateTerritoryXpValues = () => {
    const errors: Record<string, string> = {};

    Object.entries(currentTerritoryXpModalValues).forEach(([code, value]) => {
      if (
        value === "" ||
        value === null ||
        value === undefined ||
        Number(value) <= 0 ||
        isNaN(Number(value))
      ) {
        errors[code] = `${code} territory must have a positive value.`;
      }
    });

    setTerritoryXpErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const handleSaveTerritoryXpValues = () => {
    if (!validateTerritoryXpValues()) {
      showSnackbar(
        "Please provide a positive XP value for every territory.",
        "error",
      );
      return;
    }

    if (!subChallengeMetaData.isSubChallenge) {
      setChallengeDetails((prev) => ({
        ...prev,
        currencyRewardValues: currentTerritoryXpModalValues,
      }));
    } else {
      const subChallenge = challengeDetails.subChallenges[subChallengeMetaData.subChallengeIndex || 0];
      setChallengeDetails((prev) => ({...prev, subChallenges: prev.subChallenges.map((sub, idx) =>
        idx === subChallengeMetaData.subChallengeIndex
          ? { ...sub, currencyRewardValues: currentTerritoryXpModalValues }
          : sub
      )}));
    }

    closeTerritoryXpModal();
    showSnackbar("Territory XP values saved", "success");
  };

  const handleCloseTerritoryXpModal = () => {
    closeTerritoryXpModal();
  };

  // ─── Submit ─────────────────────────────────────────────
  const submit = async () => {
    try {
      setLoading(true);
      await challengeValidation.validate(challengeDetails);

      // Start can't be in the past and must end after it starts. A live challenge is
      // exempt — its schedule is locked, so its past start date is left as-is.
      const nowMs = Date.now();
      const startMs = new Date(challengeDetails.startDate).getTime();
      const endMs = new Date(challengeDetails.endDate).getTime();
      if (Number.isNaN(startMs) || Number.isNaN(endMs)) {
        throw new Error("Please select a valid start and end date.");
      }
      const isLiveNow =
        isEdit &&
        challengeDetails.isActive &&
        startMs <= nowMs &&
        endMs >= nowMs;
      if (!isLiveNow && startMs <= nowMs) {
        throw new Error("Start date and time must be in the future.");
      }
      if (endMs <= startMs) {
        throw new Error("End date and time must be after the start date.");
      }

      if (isPackRewardType(challengeDetails.rewardType)) {
        const rewardQty = Number(challengeDetails.rewardValue);
        if (!Number.isInteger(rewardQty) || rewardQty < 1) {
          throw new Error(
            "Reward quantity must be a whole number greater than 0 for PACK and MYSTERY PLAYER CARD rewards",
          );
        }
        if ((challengeDetails.rewardPacks || []).length !== rewardQty) {
          throw new Error(
            `Please select exactly ${rewardQty} pack ID${rewardQty > 1 ? "s" : ""} for the configured quantity`,
          );
        }
      }

      validateRewardConfig(challengeDetails);

      // Validate objective filters for INDIVIDUAL challenges
      if (challengeDetails.type === "INDIVIDUAL") {
        validateObjectiveFilters(
          challengeDetails.objectiveType,
          challengeDetails.objectiveFilters,
          standardOddsKeys,
        );
      }

      if (
        challengeDetails.type === "COMBO" &&
        (!challengeDetails.subChallenges ||
          challengeDetails.subChallenges.length === 0)
      ) {
        throw new Error(
          "Combo challenges must have at least one sub-challenge",
        );
      }

      if (challengeDetails.type === "COMBO" && !challengeDetails.comboTitle) {
        throw new Error("Combo Title is required for COMBO challenges");
      }

      // Validate each sub-challenge field for COMBO type
      if (challengeDetails.type === "COMBO") {
        challengeDetails.subChallenges.forEach((sub, i) => {
          const n = i + 1;
          if (!sub.objectiveType)
            throw new Error(`Sub-challenge #${n}: Objective type is required`);
          if (
            !hasFixedObjectiveTarget(sub.objectiveType) &&
            (sub.objectiveTarget === null ||
              sub.objectiveTarget === undefined ||
              Number(sub.objectiveTarget) < 1)
          )
            throw new Error(
              `Sub-challenge #${n}: Objective target must be at least 1`,
            );
          if (!sub.rewardType)
            throw new Error(`Sub-challenge #${n}: Reward type is required`);
          if (
            sub.rewardValue === null ||
            sub.rewardValue === undefined ||
            Number(sub.rewardValue) < 0
          )
            throw new Error(
              `Sub-challenge #${n}: Reward value must be 0 or greater`,
            );

          if (isPackRewardType(sub.rewardType)) {
            const subQty = Number(sub.rewardValue);
            if (!Number.isInteger(subQty) || subQty < 1) {
              throw new Error(
                `Sub-challenge #${n}: Quantity must be a whole number greater than 0 for PACK and MYSTERY PLAYER CARD rewards`,
              );
            }
            if ((sub.rewardPacks || []).length !== subQty) {
              throw new Error(
                `Sub-challenge #${n}: Please select exactly ${subQty} pack ID${subQty > 1 ? "s" : ""}`,
              );
            }
          }

          validateRewardConfig(sub, `Sub-challenge #${n}: `);

          // Validate objective filters for this sub-challenge
          validateObjectiveFilters(
            sub.objectiveType,
            sub.objectiveFilters,
            standardOddsKeys,
            `Sub-challenge #${n}: `,
          );
        });
      }

      let response;
      const payload = buildPayload();

      if (!isEdit) {
        response = await createChallenge(payload);
      } else {
        response = await updateChallenge(payload, id);
      }

      // Upload images based on presigned URLs from backend response
      const challengeData = response?.data;
      if (challengeData) {
        // 1. Main reward image (shared for all reward types including PACK)
        if (challengeData.uploadRewardImageUrl && rewardImage) {
          await upload(
            challengeData.uploadRewardImageUrl,
            rewardImage,
            rewardImageDimensions,
          );
        }

        // 2. Sub-challenge reward images
        if (challengeData.subChallenges?.length) {
          for (let si = 0; si < challengeData.subChallenges.length; si++) {
            const subData = challengeData.subChallenges[si];

            if (subData.uploadRewardImageUrl && subRewardImages[si]) {
              await upload(
                subData.uploadRewardImageUrl,
                subRewardImages[si],
                subRewardImageDimensions[si],
              );
            }
          }
        }
      }

      // Upload translation file for COMBO challenges (create mode only)
      if (
        !isEdit &&
        translationFile &&
        challengeData?._id &&
        challengeDetails.type === "COMBO"
      ) {
        await uploadContentTranslations(
          translationFile,
          "CHALLENGE",
          challengeData._id,
        );
      }

      showSnackbar(response?.message || "Challenge saved", "success");
      setLoading(false);
      navigate(configData.routes.challenges.challenges);
    } catch (error: any) {
      if (error?.inner?.length)
        error.inner.forEach((err: any) => showSnackbar(err.message, "error"));
      else showSnackbar(error.message, "error");
      setLoading(false);
    }
  };

  const buildPayload = () => {
    const isPackReward = isPackRewardType(challengeDetails.rewardType);
    const base: any = {
      type: challengeDetails.type,
      cmsTitle: challengeDetails.cmsTitle?.trim() || undefined,
      tier: challengeDetails.tier || undefined,
      rewardType: challengeDetails.rewardType,
      rewardValue: Number(challengeDetails.rewardValue),
      startDate: challengeDetails.startDate,
      endDate: challengeDetails.endDate,
      scheduleType: challengeDetails.scheduleType,
      ...(challengeDetails.repeatEveryDays
        ? { repeatEveryDays: Number(challengeDetails.repeatEveryDays) }
        : {}),
      operators: challengeDetails.operators,
      excludedOperators: challengeDetails.excludedOperators,
      territories: challengeDetails.territories || [],
      currencyRewardValues: challengeDetails.currencyRewardValues || {},
      sortOrder: challengeDetails.sortOrder
        ? Number(challengeDetails.sortOrder)
        : 0,
      isActive: challengeDetails.isActive,
      isFtueChallenge: !!challengeDetails.isFtueChallenge,
    };

    if (challengeDetails.rewardType !== "XP") {
      base.currencyRewardValues = {};
    }

    // Reward image — shared for all reward types (coins, XP, PACK, etc.)
    if (rewardImage) {
      base.fileType = "image/webp";
    }

    // rewardPacks — flat array of pack IDs
    if (isPackReward && challengeDetails.rewardPacks.length > 0) {
      base.rewardPacks = challengeDetails.rewardPacks.filter(Boolean);
    }

    if (needsRewardDuration(challengeDetails.rewardType)) {
      base.rewardDurationMinutes = Number(challengeDetails.rewardDurationMinutes);
    }
    if (needsPrizeWheel(challengeDetails.rewardType)) {
      base.prizeWheelConfigId = challengeDetails.prizeWheelConfigId;
    }

    // Combo fields
    if (challengeDetails.type === "COMBO") {
      if (challengeDetails.comboTitle) {
        base.comboTitle = challengeDetails.comboTitle;
        base.comboTitleTrans = challengeDetails.comboTitleTrans;
      }
      if (challengeDetails.comboRewardTitle) {
        base.comboRewardTitle = challengeDetails.comboRewardTitle;
        base.comboRewardTitleTrans = challengeDetails.comboRewardTitleTrans;
      }
    }

    if (challengeDetails.type === "INDIVIDUAL") {
      base.objectiveType = challengeDetails.objectiveType;
      base.objectiveTarget = hasFixedObjectiveTarget(challengeDetails.objectiveType)
        ? 1
        : Number(challengeDetails.objectiveTarget);
      base.objectiveFilters = buildFilters(
        challengeDetails.objectiveType,
        challengeDetails.objectiveFilters,
      );
    } else {
      base.subChallenges = challengeDetails.subChallenges.map((sub, idx) => {
        const isSubPackReward = isPackRewardType(sub.rewardType);
        const subEntry: any = {
          // Include _id for existing sub-challenges (edit mode) so backend can preserve images
          ...(isEdit && sub._id ? { _id: sub._id } : {}),
          tier: sub.tier || undefined,
          objectiveType: sub.objectiveType,
          objectiveTarget: hasFixedObjectiveTarget(sub.objectiveType)
            ? 1
            : Number(sub.objectiveTarget),
          objectiveFilters: buildFilters(
            sub.objectiveType,
            sub.objectiveFilters,
          ),
          rewardType: sub.rewardType,
          rewardValue: Number(sub.rewardValue),
          currencyRewardValues: sub.currencyRewardValues || {},
        };

        if(subEntry.rewardType !== "XP") {
          subEntry.currencyRewardValues = {};
        }
        // Reward image for sub-challenge
        if (subRewardImages[idx]) {
          subEntry.fileType = "image/webp";
        }
        // rewardPacks — flat array of pack IDs
        if (isSubPackReward && sub.rewardPacks?.length > 0) {
          subEntry.rewardPacks = sub.rewardPacks.filter(Boolean);
        }
        if (needsRewardDuration(sub.rewardType)) {
          subEntry.rewardDurationMinutes = Number(sub.rewardDurationMinutes);
        }
        if (needsPrizeWheel(sub.rewardType)) {
          subEntry.prizeWheelConfigId = sub.prizeWheelConfigId;
        }
        return subEntry;
      });
    }

    return base;
  };

  const buildFilters = (
    objectiveType: string,
    filters: ObjectiveFilters | null | undefined,
  ): ObjectiveFilters => {
    const safeFilters = filters || {};
    const result: ObjectiveFilters = {};
    if (needsPositionFilter(objectiveType) && safeFilters.position)
      result.position = safeFilters.position;
    if (needsNationalityFilter(objectiveType) && safeFilters.nationality)
      result.nationality = safeFilters.nationality;
    if (needsClubFilter(objectiveType) && safeFilters.clubId)
      result.clubId = safeFilters.clubId;
    if (needsMinOdds(objectiveType) && safeFilters.minOdds)
      result.minOdds = Number(safeFilters.minOdds);
    if (needsMinPlayers(objectiveType) && safeFilters.minPlayers)
      result.minPlayers = Number(safeFilters.minPlayers);
    if (needsBetTypeFilter(objectiveType) && safeFilters.betType)
      result.betType = safeFilters.betType;
    if (needsMinStake(objectiveType) && safeFilters.minStake)
      result.minStake = Number(safeFilters.minStake);
    return result;
  };

  // ─── Handlers ───────────────────────────────────────────
  const onChange = (e: any) => {
    const { name, value } = e?.target;
    let processedValue = value;

    if (
      ["objectiveTarget", "rewardValue", "sortOrder", "rewardDurationMinutes"].includes(name)
    ) {
      processedValue = value === "" ? null : Number(value);
    }

    let data: any = {
      ...challengeDetails,
      [name]: ["isActive", "isFtueChallenge"].includes(name)
        ? !challengeDetails[name]
        : processedValue,
    };

    if (name === "rewardValue") {
      debouncedFetchTerritoryXpValues(
        challengeDetails.rewardType,
        processedValue,
        false,
      );
    }

    // Reset rewardPacks / reward image when rewardType changes
    if (name === "rewardType") {
      data.rewardPacks = [];
      data.rewardImageUrl = undefined;
      data.rewardDurationMinutes = null;
      data.prizeWheelConfigId = "";
      setPackSelections([]);
      setRewardImage(null);
      setRewardImageDimensions(null);

      if (hasFixedRewardQuantity(processedValue)) {
        data.rewardValue = 1;
      }
    }

    if (
      name === "rewardType" &&
      processedValue === "XP" &&
      challengeDetails.rewardValue
    ) {
      debouncedFetchTerritoryXpValues(
        processedValue,
        challengeDetails.rewardValue,
        false,
      );
    }

    if (name === "objectiveType" && hasFixedObjectiveTarget(processedValue)) {
      data.objectiveTarget = 1;
    }

    // When switching type, preserve existing data so users can switch back without losing work.
    // Only seed a default sub-challenge when switching to COMBO with none present.
    if (name === "type") {
      if (value === "COMBO" && data.subChallenges.length === 0) {
        data.subChallenges = [{ ..._emptySubChallenge }];
      }
    }

    // Clear repeatEveryDays when switching away from CUSTOM
    if (name === "scheduleType" && value !== "CUSTOM") {
      data.repeatEveryDays = null;
    }

    setChallengeDetails(data);
  };

  const onFilterChange = (field: string, value: any) => {
    setChallengeDetails((prev) => ({
      ...prev,
      objectiveFilters: { ...prev.objectiveFilters, [field]: value },
    }));
  };

  const onDateChange = (name: string, value: any) => {
    const isoValue = value ? value.toISOString() : "";
    setChallengeDetails((prev) => ({ ...prev, [name]: isoValue }));
  };

  // ─── Sub-challenge handlers ─────────────────────────────
  const addSubChallenge = () => {
    setChallengeDetails((prev) => ({
      ...prev,
      subChallenges: [...prev.subChallenges, { ..._emptySubChallenge }],
    }));
    setSubRewardImages((prev) => [...prev, null]);
    setSubRewardImageDimensions((prev) => [...prev, null]);
    setSubPackSelections((prev) => [...prev, []]);
  };

  const removeSubChallenge = (index: number) => {
    setChallengeDetails((prev) => ({
      ...prev,
      subChallenges: prev.subChallenges.filter((_, i) => i !== index),
    }));
    setSubRewardImages((prev) => prev.filter((_, i) => i !== index));
    setSubRewardImageDimensions((prev) => prev.filter((_, i) => i !== index));
    setSubPackSelections((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubChallengeChange = (index: number, field: string, value: any) => {
    if (
      challengeDetails.rewardType !== "XP" ||
      challengeDetails.subChallenges[index].rewardType !== "XP"
    ) {
      setCurrentTerritoryXpModalValues({});
      setTerritoryXpErrors({});
      setIsTerritoryXpLoading(false);
    }
    setChallengeDetails((prev) => {
      const subs = [...prev.subChallenges];
      const sub = { ...subs[index] };

      if (
        ["objectiveTarget", "rewardValue", "rewardDurationMinutes"].includes(field)
      ) {
        sub[field] = value === "" ? null : Number(value);
      } else {
        sub[field] = value;
      }

      if (field === "objectiveType") {
        sub.objectiveFilters = {};
        if (hasFixedObjectiveTarget(value)) {
          sub.objectiveTarget = 1;
        }
      }

      if (field === "rewardValue") {
        debouncedFetchTerritoryXpValues(sub.rewardType, sub[field], true, index);
      }

      // Reset rewardPacks / reward image when rewardType changes
      if (field === "rewardType") {
        sub.rewardPacks = [];
        sub.rewardImageUrl = undefined;
        sub.rewardDurationMinutes = null;
        sub.prizeWheelConfigId = "";
        if (hasFixedRewardQuantity(value)) {
          sub.rewardValue = 1;
        }
        setSubPackSelections((prev) => {
          const next = [...prev];
          next[index] = [];
          return next;
        });
        setSubRewardImages((prev) => {
          const next = [...prev];
          next[index] = null;
          return next;
        });
        setSubRewardImageDimensions((prev) => {
          const next = [...prev];
          next[index] = null;
          return next;
        });
      }

      if (field === "rewardType" && sub[field] === "XP" && sub.rewardValue) {
        debouncedFetchTerritoryXpValues(sub.rewardType, sub.rewardValue, true, index);
      }

      subs[index] = sub;
      return { ...prev, subChallenges: subs };
    });
  };

  const onSubFilterChange = (index: number, field: string, value: any) => {
    setChallengeDetails((prev) => {
      const subs = [...prev.subChallenges];
      subs[index] = {
        ...subs[index],
        objectiveFilters: {
          ...(subs[index].objectiveFilters || {}),
          [field]: value,
        },
      };
      return { ...prev, subChallenges: subs };
    });
  };

  // ─── Image ─────────────────────────────────────────────
  const upload = async (url: string, img: any, dimensions?: any) => {
    const defaultDims =
      DEFAULT_IMAGE_DIMENSIONS[ImageType.CHALLENGE_REWARD_IMAGE];
    try {
      const resp = await uploadImageV2({
        data: img,
        uploadUrl: url,
        resize: true,
        resizeToWidth: dimensions?.targetWidth || defaultDims.width,
        resizeToHeight: dimensions?.targetHeight || defaultDims.height,
      });
      if (resp.status) {
        showSnackbar(resp.message ?? "Image Uploaded", "success");
        return resp;
      } else {
        throw new Error(
          resp.message || `Image upload failed with status ${resp.status}`,
        );
      }
    } catch (error: any) {
      showSnackbar(error.message || "Error uploading image!", "error");
      throw error;
    }
  };

  // ─── Fetch ─────────────────────────────────────────────
  const fetchChallengeInfo = async () => {
    try {
      setLoading(true);
      const resp = await getChallengeInfo(id);
      if (resp?.data) {
        const subChallenges = (resp.data.subChallenges || []).map(
          (sub: any) => {
            return {
              ...sub,
              tier: sub.tier || "",
              objectiveFilters: normalizeObjectiveFilters(sub.objectiveFilters),
              rewardPacks: (sub.rewardPacks || []).map((p: any) =>
                typeof p === "string" ? p : p?.packId || p,
              ),
              rewardImageUrl: withCacheBuster(sub.rewardImageUrl),
              rewardDurationMinutes: sub.rewardDurationMinutes ?? null,
              prizeWheelConfigId: sub.prizeWheelConfigId || "",
            };
          },
        );

        setChallengeDetails({
          ..._challengeDetails,
          ...resp.data,
          operators: resp.data.operators || [],
          excludedOperators: resp.data.excludedOperators || [],
          territories: resp.data.territories || [],
          currencyRewardValues: resp.data.currencyRewardValues || {},
          subChallenges,
          objectiveFilters: normalizeObjectiveFilters(
            resp.data.objectiveFilters,
          ),
          rewardPacks: (resp.data.rewardPacks || []).map((p: any) =>
            typeof p === "string" ? p : p?.packId || p,
          ),
          rewardImageUrl: withCacheBuster(resp.data.rewardImageUrl),
          rewardDurationMinutes: resp.data.rewardDurationMinutes ?? null,
          prizeWheelConfigId: resp.data.prizeWheelConfigId || "",
          cmsTitle: resp.data.cmsTitle || "",
          comboTitle: resp.data.comboTitle || "",
          comboTitleTrans: resp.data.comboTitleTrans || {},
          comboRewardTitle: resp.data.comboRewardTitle || "",
          comboRewardTitleTrans: resp.data.comboRewardTitleTrans || {},
        });
        // Sync image arrays (no actual files yet — existing images are URLs)
        setRewardImage(null);
        setSubRewardImages(
          new Array((resp.data.subChallenges || []).length).fill(null),
        );

        // Resolve labels for pre-selected packs (edit mode)
        const resolvePackLabel = async (pid: string): Promise<PackOption | null> => {
          if (!pid) return null;
          try {
            const info = await packInfo(pid);
            if (info?.data) return toPackOption(info.data);
          } catch {}
          return { id: pid, label: pid };
        };

        const mainPackIds = (resp.data.rewardPacks || []).map((p: any) =>
          typeof p === "string" ? p : p?.packId || p,
        );
        const resolvedMain = await Promise.all(
          mainPackIds.map(resolvePackLabel),
        );
        setPackSelections(resolvedMain);
        setPackOptions(
          (prev) =>
            getUniquesArrayObject([
              ...(resolvedMain.filter(Boolean) as PackOption[]),
              ...prev,
            ]) as PackOption[],
        );

        const resolvedSubs = await Promise.all(
          (resp.data.subChallenges || []).map(async (sub: any) => {
            const subPackIds = (sub.rewardPacks || []).map((p: any) =>
              typeof p === "string" ? p : p?.packId || p,
            );
            return await Promise.all(subPackIds.map(resolvePackLabel));
          }),
        );
        setSubPackSelections(resolvedSubs);
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  // ─── Translation Modal ─────────────────────────────────
  const getTranslatedData = (field: string, subIdx?: number | null) => {
    const source =
      subIdx != null
        ? challengeDetails.subChallenges[subIdx]?.[field] || {}
        : challengeDetails?.[field] || {};
    const _translatedData = Object.entries(source).map(([key, value]) => ({
      lang: key,
      inputValue: value,
    }));
    return _translatedData || [];
  };

  const handleOpenModal = (
    type: string,
    field: string,
    subIdx?: number | null,
  ) => {
    setModalData((state) => {
      if (type === "view") {
        let data = getTranslatedData(field, subIdx);
        return {
          ...state,
          viewData: data,
          field,
          modal: type,
          subChallengeIndex: subIdx ?? null,
        };
      } else {
        return {
          ...state,
          field,
          modal: type,
          language: "",
          inputValue: "",
          subChallengeIndex: subIdx ?? null,
        };
      }
    });
    open();
  };

  const handleModalChange = (event: any) => {
    const { name, value } = event.target;
    setModalData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLangEditClick = (data: any) => {
    close();
    setModalData((state) => ({
      ...state,
      language: data?.lang,
      inputValue: data?.inputValue,
      modal: "add",
    }));
    open();
  };

  const handleLangDeleteClick = (data: any) => {
    if (data.lang === "en") {
      showSnackbar(
        "Cannot delete English translation, as it's a default language",
        "error",
      );
      return;
    }

    setModalData((state) => ({
      ...state,
      viewData: state.viewData.filter((item) => item.lang !== data.lang),
    }));

    const subIdx = modalData.subChallengeIndex;
    setChallengeDetails((prev) => {
      if (subIdx != null) {
        const subs = [...prev.subChallenges];
        const sub = { ...subs[subIdx] };
        const fieldKey = modalData.field!;
        sub[fieldKey] = Object.keys(sub[fieldKey] || {}).reduce(
          (acc: any, key: string) => {
            if (key !== data.lang) acc[key] = (sub[fieldKey] as any)[key];
            return acc;
          },
          {},
        );
        subs[subIdx] = sub;
        return { ...prev, subChallenges: subs };
      }
      const fieldKey = modalData.field!;
      prev[fieldKey] = Object.keys(prev[fieldKey]).reduce(
        (acc: any, key: string) => {
          if (key !== data.lang) {
            acc[key] = prev[fieldKey][key];
          }
          return acc;
        },
        {},
      );
      return { ...prev };
    });
  };

  const handleAddTranslation = () => {
    const { field, subChallengeIndex: subIdx } = modalData;
    if (!field) {
      showSnackbar("Confused which field to add the translation", "error");
      return;
    }

    if (subIdx != null) {
      // Sub-challenge translation
      setChallengeDetails((prev) => {
        const subs = [...prev.subChallenges];
        const sub = { ...subs[subIdx] };
        sub[field] = {
          ...((sub[field] as any) || {}),
          [modalData.language]: modalData.inputValue,
        };
        if (modalData.language === "en" && modalData.inputValue !== "") {
          const baseField = field.split("Trans")[0];
          sub[baseField] = modalData.inputValue;
        }
        subs[subIdx] = sub;
        return { ...prev, subChallenges: subs };
      });
    } else {
      // Main challenge translation
      setChallengeDetails((prev) => ({
        ...prev,
        [field]: {
          ...prev[field],
          [modalData.language]: modalData.inputValue,
        },
      }));

      if (modalData.language === "en" && modalData.inputValue !== "") {
        const baseField = field.split("Trans")[0];
        setChallengeDetails((details) => ({
          ...details,
          [baseField]: modalData.inputValue,
          [field]: { ...details[field], en: modalData.inputValue },
        }));
      }
    }

    setModalData((prev) => ({
      ...prev,
      inputValue: "",
      language: "",
      field: null,
      subChallengeIndex: null,
    }));
    close();
  };

  const fetchStandardOddsKeys = async () => {
    try {
      const keys = await getStandardOddsKeys();
      if (keys?.length) setStandardOddsKeys(new Set(keys));
    } catch {
      // Silently fail — validation will be skipped if keys cannot be loaded
    }
  };

  const fetchRewardImages = async () => {
    try {
      const resp = await getChallengeRewardImages();
      // Deterministic S3 keys reuse the same URL when a default is replaced; bust the cache
      // so the preview reflects the latest default image.
      const stamp = Date.now();
      const map: Record<string, string | null> = {};
      (resp?.data || []).forEach((item: any) => {
        map[item.rewardType] = item.imageUrl
          ? `${item.imageUrl.split("?")[0]}?t=${stamp}`
          : null;
      });
      setRewardImageMap(map);
    } catch {
      // Silently fail — the default-image preview / warning simply won't show
    }
  };

  const fetchObjectiveTypeConfigs = async () => {
    try {
      const resp = await getConfigurations();
      if (resp?.data?.length) {
        const otConfig = resp.data.find(
          (c: any) => c.type === "OBJECTIVE_TYPE_CONFIG",
        );
        const configs = otConfig?.data?.objectiveTypes || [];
        setObjectiveTypeOptions(
          configs.map((c: any) => ({
            id: c.name,
            label: c.name.replace(/_/g, " "),
          })),
        );
      }
    } catch {
      // Silently fail — options will be empty
    }
  };

  const fetchPrizeWheelOptions = async () => {
    try {
      const resp = await getChallengePrizeWheels({ skipPagination: true, isActive: true });
      const items = resp?.data?.items || [];
      setPrizeWheelOptions(
        items.map((wheel: any) => ({
          id: wheel._id,
          label: `${wheel.name} (${wheel.segments?.length ?? 0} segments)`,
        })),
      );
    } catch {
    }
  };

  const fetchObjectiveTypeTitlesMap = async () => {
    try {
      const resp = await getObjectiveTypeTitles();
      const items = resp?.data || [];
      const map = items.reduce((acc: Record<string, string>, item: any) => {
        const objectiveType = item?.objectiveType;
        const titleTemplate =
          item?.titleTemplateTrans?.en || item?.titleTemplate || "";
        if (objectiveType && titleTemplate) {
          acc[objectiveType] = titleTemplate;
        }
        return acc;
      }, {});
      setObjectiveTypeTitleMap(map);
    } catch {
      // Keep local fallback examples if objective type titles cannot be loaded.
      setObjectiveTypeTitleMap({});
    }
  };

  const getOptionLabel = (
    options: { id: string; label: string }[],
    id?: string,
    fallback?: string,
  ) => {
    if (!id) return fallback || "";
    const found = options.find((opt) => opt.id === id);
    return found?.label || fallback || id;
  };

  const getObjectiveDynamicValues = (
    objectiveType: string,
    objectiveTarget: number | null,
    filters: ObjectiveFilters | null | undefined,
  ): string[] => {
    const safeFilters = filters || {};
    const targetValue = String(objectiveTarget ?? "N");
    const minPlayersValue = String(safeFilters.minPlayers ?? 3);
    const minOddsValue = String(safeFilters.minOdds ?? 2.0);
    const minStakeValue = String(safeFilters.minStake ?? 20);
    const positionValue = getOptionLabel(
      positionGroupOptions,
      safeFilters.position,
      "Defender",
    );
    const nationalityValue = getOptionLabel(
      sessionHelperInstance.countryNameOptions || [],
      safeFilters.nationality,
      "English",
    );
    const clubValue = getOptionLabel(
      sessionHelperInstance.clubsOptions || [],
      safeFilters.clubId,
      "Arsenal",
    );
    const betTypeValue = getOptionLabel(
      bettingMarketOptions,
      safeFilters.betType,
      "Correct Score",
    );

    const map: Record<string, string[]> = {
      GOALS_SCORED: [targetValue],
      GOALS_BY_POSITION: [targetValue, positionValue],
      GOALS_BY_NATIONALITY: [targetValue, nationalityValue],
      GOALS_BY_CLUB: [targetValue, clubValue],
      CONSECUTIVE_WINS: [targetValue],
      CLEAN_SHEETS: [targetValue],
      CONSECUTIVE_CLEAN_SHEETS: [targetValue],
      CONSECUTIVE_SCORING: [targetValue],
      WINS_WITH_NATIONALITY: [targetValue, minPlayersValue, nationalityValue],
      WINS_WITH_CLUB: [targetValue, minPlayersValue, clubValue],
      BETS_PLACED: [targetValue],
      BETS_PLACED_MARKET: [targetValue, betTypeValue],
      BET_WIN_BY_ODDS: [targetValue, minOddsValue],
      LEAGUE_POINTS: [targetValue],
      VAULT_LEVEL_UP: [targetValue],
      STAKE_ON_MATCHES: [minStakeValue, targetValue],
      STAKE_IN_TIME_PERIOD: [targetValue],
    };

    return map[objectiveType] || [targetValue];
  };

  const getFallbackDynamicExample = (
    objectiveType: string,
    objectiveTarget: number | null,
    filters: ObjectiveFilters | null | undefined,
  ) => {
    const baseExample = objectiveTypeDescriptions[objectiveType]?.example;
    if (!baseExample) return "";

    let resolved = baseExample.replace("N", String(objectiveTarget ?? "N"));
    const values = getObjectiveDynamicValues(
      objectiveType,
      objectiveTarget,
      filters,
    );

    if (objectiveType === "GOALS_BY_POSITION") {
      resolved = resolved.replace("Defender", values[1] || "Defender");
    }

    if (objectiveType === "GOALS_BY_NATIONALITY") {
      resolved = resolved.replace(
        "English players",
        `${values[1] || "English"} players`,
      );
    }

    if (objectiveType === "GOALS_BY_CLUB") {
      resolved = resolved.replace(
        "Arsenal players",
        `${values[1] || "Arsenal"} players`,
      );
    }

    if (objectiveType === "WINS_WITH_NATIONALITY") {
      resolved = resolved.replace(
        "3 Brazilian players",
        `${values[1] || "3"} ${values[2] || "Brazilian"} players`,
      );
    }

    if (objectiveType === "WINS_WITH_CLUB") {
      resolved = resolved.replace(
        "3 Arsenal players",
        `${values[1] || "3"} ${values[2] || "Arsenal"} players`,
      );
    }

    if (objectiveType === "BETS_PLACED_MARKET") {
      resolved = resolved.replace(
        "Correct Score markets",
        `${values[1] || "Correct Score"} markets`,
      );
    }

    if (objectiveType === "BET_WIN_BY_ODDS") {
      resolved = resolved.replace("2.0", values[1] || "2.0");
    }

    return resolved;
  };

  useEffect(() => {
    fetchObjectiveTypeConfigs();
    fetchObjectiveTypeTitlesMap();
    fetchStandardOddsKeys();
    fetchRewardImages();
    fetchPrizeWheelOptions();
    if (isEdit) fetchChallengeInfo();
    // Fetch packs for reward selection
    fetchPacks();
  }, []);

  const fetchPacks = async () => {
    try {
      const resp = await getPlayerPacks({
        pageNo: defaultPageNo,
        pageSize: defaultPageSize,
        ...REWARD_PACK_FILTER,
      });
      if (resp?.items) {
        setPackOptions(resp.items.map(toPackOption));
      }
    } catch (err: any) {
      console.error("Failed to load packs:", err);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handlePackSearch = useCallback(
    debounce(async (searchValue: string) => {
      try {
        const resp = await getPlayerPacks(
          searchValue
            ? { search: searchValue, skipPagination: true, ...REWARD_PACK_FILTER }
            : { pageNo: defaultPageNo, pageSize: defaultPageSize, ...REWARD_PACK_FILTER },
        );
        if (resp?.items) {
          setPackOptions(resp.items.map(toPackOption));
        }
      } catch (err: any) {
        console.error("Pack search error:", err);
      }
    }, 300),
    [],
  );

  // ─── Conditional Filters Renderer ──────────────────────
  const renderObjectiveFilters = (
    objectiveType: string,
    filters: ObjectiveFilters,
    onFilterUpdate: (field: string, value: any) => void,
    objectiveTarget?: number | null,
  ) => {
    return (
      <>
        {needsPositionFilter(objectiveType) && (
          <Box className={styles.field}>
            <SimpleSelectInput
              label="Position Group"
              name="position"
              value={filters.position || ""}
              options={positionGroupOptions}
              onChange={(e: any) =>
                onFilterUpdate("position", e.target.value || undefined)
              }
            />
          </Box>
        )}

        {needsNationalityFilter(objectiveType) && (
          <>
            <Box className={styles.field}>
              <SearchableInput
                label="Nationality"
                name="nationality"
                selected={
                  filters.nationality
                    ? (() => {
                        const opt = (
                          sessionHelperInstance.countryNameOptions || []
                        ).find((o: any) => o.id === filters.nationality);
                        return (
                          opt || {
                            id: filters.nationality,
                            label: filters.nationality,
                          }
                        );
                      })()
                    : null
                }
                options={sessionHelperInstance.countryNameOptions || []}
                onOptionSelect={(e: any) => {
                  const val = e.target.value;
                  const id = Array.isArray(val)
                    ? val[0]?.id || val[0]
                    : val?.id || val;
                  onFilterUpdate("nationality", id || undefined);
                }}
                isMultiSelect={false}
                placeholder="Search nationality"
              />
            </Box>
            {needsMinPlayers(objectiveType) && (
              <Box className={styles.field}>
                <SimpleTextInput
                  label="Min Players from Nationality"
                  name="minPlayers"
                  type="number"
                  value={filters.minPlayers ?? ""}
                  onChange={(e: any) =>
                    onFilterUpdate(
                      "minPlayers",
                      e.target.value === "" ? null : Number(e.target.value),
                    )
                  }
                  placeholder="1-11"
                />
              </Box>
            )}
          </>
        )}

        {needsClubFilter(objectiveType) && (
          <>
            <Box className={styles.field}>
              <SearchableInput
                label="Club"
                name="clubId"
                selected={
                  filters.clubId
                    ? (() => {
                        const opt = (
                          sessionHelperInstance.clubsOptions || []
                        ).find((o: any) => o.id === filters.clubId);
                        return (
                          opt || { id: filters.clubId, label: filters.clubId }
                        );
                      })()
                    : null
                }
                options={sessionHelperInstance.clubsOptions || []}
                onOptionSelect={(e: any) => {
                  const val = e.target.value;
                  const id = Array.isArray(val)
                    ? val[0]?.id || val[0]
                    : val?.id || val;
                  onFilterUpdate("clubId", id || undefined);
                }}
                isMultiSelect={false}
                placeholder="Search club"
              />
            </Box>
            {needsMinPlayers(objectiveType) && (
              <Box className={styles.field}>
                <SimpleTextInput
                  label="Min Players from Club"
                  name="minPlayers"
                  type="number"
                  value={filters.minPlayers ?? ""}
                  onChange={(e: any) =>
                    onFilterUpdate(
                      "minPlayers",
                      e.target.value === "" ? null : Number(e.target.value),
                    )
                  }
                  placeholder="1-11"
                />
              </Box>
            )}
          </>
        )}

        {needsMinOdds(objectiveType) && (
          <>
            <Box className={styles.field}>
              <SimpleTextInput
                label="Minimum Odds"
                name="minOdds"
                type="number"
                value={filters.minOdds ?? ""}
                onChange={(e: any) =>
                  onFilterUpdate(
                    "minOdds",
                    e.target.value === "" ? null : Number(e.target.value),
                  )
                }
                placeholder="e.g. 2.00"
              />
            </Box>
            {filters.minOdds != null &&
              filters.minOdds !== ("" as any) &&
              standardOddsKeys.size > 0 &&
              !standardOddsKeys.has(String(filters.minOdds)) && (
                <Box sx={{ width: "100%", mt: 1 }}>
                  <Alert severity="warning">
                    {filters.minOdds} is not a valid standard odds value. The
                    fractional display will be undefined in-game. Please use a
                    value from the standard odds table (e.g.{" "}
                    {getNearbyOddsExamples(filters.minOdds, standardOddsKeys)}
                    ).
                  </Alert>
                </Box>
              )}
          </>
        )}

        {needsBetTypeFilter(objectiveType) && (
          <Box className={styles.field}>
            <SimpleSelectInput
              label="Betting Market Type"
              name="betType"
              value={filters.betType ?? ""}
              options={bettingMarketOptions}
              onChange={(e: any) =>
                onFilterUpdate("betType", e.target.value || undefined)
              }
            />
          </Box>
        )}

        {needsMinStake(objectiveType) && (
          <>
            <Box className={styles.field}>
              <SimpleTextInput
                label="Minimum Stake per Match"
                name="minStake"
                type="number"
                value={filters.minStake ?? ""}
                onChange={(e: any) =>
                  onFilterUpdate(
                    "minStake",
                    e.target.value === "" ? null : Number(e.target.value),
                  )
                }
                placeholder="e.g. 20"
              />
            </Box>
            <Box sx={{ width: "100%", mt: 1 }}>
              <Alert severity="info">
                Objective Target is the number of matches; a match counts only
                when its total stake (across all bets on that match) is strictly
                MORE than this amount — staking exactly this does not qualify.
                The amount is in the player&apos;s own currency and is not
                converted — target a territory if that matters.
              </Alert>
            </Box>
          </>
        )}

        {objectiveType === "STAKE_IN_TIME_PERIOD" && (
          <Box sx={{ width: "100%", mt: 1 }}>
            <Alert severity="info">
              Objective Target is the total amount the player must stake before
              the challenge expires — the challenge&apos;s own duration is the
              time limit, so there is no separate time to set. Each match&apos;s
              stake accumulates until the target is reached. The amount is in the
              player&apos;s own currency and is not converted — target a
              territory if that matters.
            </Alert>
          </Box>
        )}

        {objectiveType === "GOALS_IN_SINGLE_MATCH" &&
          Number(objectiveTarget) >= MAX_GOALS_PER_MATCH && (
            <Box sx={{ width: "100%", mt: 1 }}>
              <Alert severity="warning">
                A player can score at most {MAX_GOALS_PER_MATCH} goals in a
                single match, and this objective needs strictly MORE than the
                target (target 5 ⇒ needs 6). With Objective Target{" "}
                {MAX_GOALS_PER_MATCH} or higher no player can ever complete it —
                set it to {MAX_GOALS_PER_MATCH - 1} or lower. (This is only a
                warning; the challenge can still be saved.)
              </Alert>
            </Box>
          )}
      </>
    );
  };

  // ─── Reward-type specific configuration ────────────────
  const renderRewardExtras = (
    reward: {
      rewardType: string;
      rewardDurationMinutes?: number | null;
      prizeWheelConfigId?: string;
    },
    onRewardUpdate: (field: string, value: any) => void,
  ) => (
    <>
      {needsRewardDuration(reward.rewardType) &&
        (() => {
          const dur = decomposeDuration(reward.rewardDurationMinutes);
          const totalMinutes = composeDuration(dur.days, dur.hours, dur.minutes);
          const updatePart = (part: "days" | "hours" | "minutes", raw: string) => {
            const value = raw === "" ? 0 : Math.max(0, Math.floor(Number(raw) || 0));
            const next = { ...dur, [part]: value };
            const total = composeDuration(next.days, next.hours, next.minutes);
            onRewardUpdate("rewardDurationMinutes", total > 0 ? total : null);
          };
          return (
            <>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", width: "100%" }}>
                <Box sx={{ flex: 1, minWidth: 120 }}>
                  <SimpleTextInput
                    label="Days"
                    name="rewardDurationDays"
                    type="number"
                    value={dur.days}
                    onChange={(e: any) => updatePart("days", e.target.value)}
                    placeholder="0"
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 120 }}>
                  <SimpleTextInput
                    label="Hours"
                    name="rewardDurationHours"
                    type="number"
                    value={dur.hours}
                    onChange={(e: any) => updatePart("hours", e.target.value)}
                    placeholder="0"
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 120 }}>
                  <SimpleTextInput
                    label="Minutes"
                    name="rewardDurationMinutes"
                    type="number"
                    value={dur.minutes}
                    onChange={(e: any) => updatePart("minutes", e.target.value)}
                    placeholder="0"
                  />
                </Box>
              </Box>
              <Box sx={{ width: "100%", mt: 1 }}>
                <Alert severity={totalMinutes > 0 ? "info" : "warning"}>
                  Set the multiplier duration as Days + Hours + Minutes (use any
                  combination). Total: <b>{totalMinutes} minute(s)</b>
                  {totalMinutes > 0 ? "" : " — must be at least 1 minute."}
                  <br />
                  Reward Value is the multiplier itself (3 = 3x XP). Claiming
                  grants nothing up front — for this duration afterwards, all XP
                  the player earns from gameplay is multiplied. XP paid out by
                  challenge rewards is never multiplied.
                </Alert>
              </Box>
            </>
          );
        })()}

      {needsPrizeWheel(reward.rewardType) && (
        <>
          <Box className={styles.field}>
            <SimpleSelectInput
              label="Prize Wheel"
              name="prizeWheelConfigId"
              value={reward.prizeWheelConfigId ?? ""}
              options={prizeWheelOptions}
              onChange={(e: any) =>
                onRewardUpdate("prizeWheelConfigId", e.target.value || "")
              }
            />
          </Box>
          <Box sx={{ width: "100%", mt: 1 }}>
            <Alert severity={prizeWheelOptions.length ? "info" : "warning"}>
              {prizeWheelOptions.length
                ? "Coin values and drop rates live on the wheel — configure them under Challenges → Prize Wheels. The winning slice is drawn server-side when the player claims."
                : "No active prize wheels exist yet. Create one under Challenges → Prize Wheels before saving this reward."}
            </Alert>
          </Box>
        </>
      )}
    </>
  );

  // ─── Render ──────────────────────────────────────────────
  const isLive =
    isEdit &&
    challengeDetails.isActive &&
    !!challengeDetails.startDate &&
    !!challengeDetails.endDate &&
    new Date(challengeDetails.startDate) <= new Date() &&
    new Date(challengeDetails.endDate) >= new Date();
  const isComboType = challengeDetails.type === "COMBO";
  const rewardPreview = resolveRewardPreview(
    challengeDetails.rewardType,
    challengeDetails.rewardImageUrl,
    packSelections,
    rewardImageMap,
  );
  const subRewardPreviews = (challengeDetails.subChallenges ?? []).map(
    (sub: SubChallenge, idx: number) =>
      resolveRewardPreview(
        sub.rewardType,
        sub.rewardImageUrl,
        subPackSelections[idx],
        rewardImageMap,
      ),
  );
  const rewardSectionStep = isComboType ? 4 : 3;
  const scheduleSectionStep = isComboType ? 5 : 4;
  const settingsSectionStep = isComboType ? 6 : 5;

  const territoryEntries = useMemo(
    () => Object.entries(currentTerritoryXpModalValues),
    [currentTerritoryXpModalValues],
  );

  return (
    <ContentShell
      showBackButton
      onBackButtonClick={cancel}
      title={`${isEdit ? "Edit" : "Create"} Challenge`}
      loading={loading}
      actions={
        <Box className={styles.buttonContainer}>
          <Button variant="outlined" color="inherit" onClick={cancel}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" onClick={submit}>
            Save Challenge
          </Button>
        </Box>
      }
    >
      <Box sx={{ maxWidth: 960, pb: 4 }}>
        {/* Banner for live challenges — partial editing allowed */}
        {isLive && (
          <Alert severity="info" sx={{ mb: 3 }}>
            This challenge is currently live. You can still update{" "}
            <strong>
              operators, excluded operators, territories, titles & translations,
              reward images (including sub-challenge images), the enabled
              state, and the claimable during FTUE setting
            </strong>
            . All other fields (objective, reward type/value, packs, dates,
            sub-challenge structure) are locked while the challenge is active.
          </Alert>
        )}
        {/* ── 1. Basic Information ─────────────────────────── */}
        <SectionCard
          step={1}
          icon={<PersonOutlineIcon fontSize="small" />}
          title="Basic Information"
          accent="#1565c0"
          locked={isLive}
        >
          {/* CMS Title — internal label to identify this challenge in the list & search */}
          <Box sx={{ mb: 3, width: "100%", maxWidth: 640 }}>
            <SimpleTextInput
              label="CMS Title (optional)"
              name="cmsTitle"
              value={challengeDetails.cmsTitle}
              onChange={onChange}
              placeholder="e.g. Weekly – Score 5 goals (coins)"
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.5 }}
            >
              Internal label only — helps you find this challenge in the list
              and search. Never shown to players.
            </Typography>
          </Box>

          {/* Challenge Type — Visual Cards */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              fontWeight={600}
              sx={{ mb: 1.5 }}
            >
              Challenge Type
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              {/* Individual Card */}
              <Paper
                elevation={0}
                onClick={() =>
                  onChange({
                    target: { name: "type", value: "INDIVIDUAL" },
                  } as any)
                }
                sx={{
                  flex: "1 1 200px",
                  maxWidth: 340,
                  p: 2,
                  cursor: "pointer",
                  borderRadius: 2.5,
                  border: "2px solid",
                  borderColor:
                    challengeDetails.type === "INDIVIDUAL"
                      ? "#1565c0"
                      : "#e0e0e0",
                  bgcolor:
                    challengeDetails.type === "INDIVIDUAL"
                      ? "#e3f2fd"
                      : "white",
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: "#1565c0",
                    bgcolor: "#e3f2fd80",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      bgcolor:
                        challengeDetails.type === "INDIVIDUAL"
                          ? "#1565c0"
                          : "#e0e0e0",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.2s",
                    }}
                  >
                    <FlagOutlinedIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Individual
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Single objective challenge
                    </Typography>
                  </Box>
                  {challengeDetails.type === "INDIVIDUAL" && (
                    <CheckCircleIcon
                      sx={{ ml: "auto", color: "#1565c0", fontSize: 20 }}
                    />
                  )}
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ lineHeight: 1.5 }}
                >
                  Player completes one specific objective to earn the reward.
                </Typography>
              </Paper>

              {/* Combo Card */}
              <Paper
                elevation={0}
                onClick={() =>
                  onChange({
                    target: { name: "type", value: "COMBO" },
                  } as any)
                }
                sx={{
                  flex: "1 1 200px",
                  maxWidth: 340,
                  p: 2,
                  cursor: "pointer",
                  borderRadius: 2.5,
                  border: "2px solid",
                  borderColor:
                    challengeDetails.type === "COMBO" ? "#6a1b9a" : "#e0e0e0",
                  bgcolor:
                    challengeDetails.type === "COMBO" ? "#f3e5f5" : "white",
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: "#6a1b9a",
                    bgcolor: "#f3e5f580",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      bgcolor:
                        challengeDetails.type === "COMBO"
                          ? "#6a1b9a"
                          : "#e0e0e0",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.2s",
                    }}
                  >
                    <LayersIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Combo
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Multi-step challenge
                    </Typography>
                  </Box>
                  {challengeDetails.type === "COMBO" && (
                    <CheckCircleIcon
                      sx={{ ml: "auto", color: "#6a1b9a", fontSize: 20 }}
                    />
                  )}
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ lineHeight: 1.5 }}
                >
                  Bundle multiple sub-challenges. Main reward granted only after
                  all sub-challenges are completed.
                </Typography>
              </Paper>
            </Box>
          </Box>

          {/* Tier — Color-coded Chips */}
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              fontWeight={600}
              sx={{ mb: 1.5 }}
            >
              Tier
              {challengeDetails.type === "COMBO" ? " (optional for Combo)" : ""}
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              {challengeTierOptions.map((opt) => {
                const vis = tierVisuals[opt.id] || tierVisuals.OTHER;
                const isSelected = challengeDetails.tier === opt.id;
                return (
                  <Chip
                    key={opt.id}
                    label={`${vis.icon}  ${opt.label}`}
                    clickable
                    onClick={() =>
                      onChange({
                        target: {
                          name: "tier",
                          value: isSelected ? "" : opt.id,
                        },
                      } as any)
                    }
                    sx={{
                      px: 1,
                      height: 36,
                      fontSize: 13,
                      fontWeight: 600,
                      borderRadius: 2,
                      border: "2px solid",
                      borderColor: isSelected ? vis.border : "#e0e0e0",
                      bgcolor: isSelected ? vis.bg : "white",
                      color: isSelected ? vis.text : "#616161",
                      transition: "all 0.2s",
                      "&:hover": {
                        borderColor: vis.border,
                        bgcolor: vis.bg,
                      },
                    }}
                  />
                );
              })}
            </Box>
            {challengeDetails.type === "COMBO" && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1 }}
              >
                Each sub-challenge has its own Tier. Root tier is optional for
                Combo challenges.
              </Typography>
            )}
          </Box>
        </SectionCard>

        {/* ── 2a. Objective Configuration (INDIVIDUAL) ─────── */}
        {challengeDetails.type === "INDIVIDUAL" && (
          <SectionCard
            step={2}
            icon={<FlagOutlinedIcon fontSize="small" />}
            title="Objective Configuration"
            subtitle="Define what the player needs to accomplish"
            accent="#6a1b9a"
            locked={isLive}
            isComplete={
              !!challengeDetails.objectiveType &&
              (challengeDetails.objectiveTarget ?? 0) > 0
            }
          >
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
              <Box className={styles.field}>
                <SimpleSelectInput
                  label="Objective Type"
                  name="objectiveType"
                  value={challengeDetails.objectiveType}
                  options={objectiveTypeOptions}
                  onChange={onChange}
                />
              </Box>
              {!hasFixedObjectiveTarget(challengeDetails.objectiveType) && (
                <Box className={styles.field}>
                  <SimpleTextInput
                    label="Objective Target"
                    name="objectiveTarget"
                    type="number"
                    value={challengeDetails.objectiveTarget ?? ""}
                    onChange={onChange}
                    placeholder="e.g. 5"
                  />
                </Box>
              )}
            </Box>

            {hasFixedObjectiveTarget(challengeDetails.objectiveType) && (
              <Box sx={{ width: "100%", mb: 2 }}>
                <Alert severity="info">
                  This objective completes as soon as the player performs the
                  action once, so there is no target to set — it is fixed at 1.
                </Alert>
              </Box>
            )}

            {/* Dynamic Example Card — Prominent */}
            {challengeDetails.objectiveType &&
              objectiveTypeDescriptions[challengeDetails.objectiveType] && (
                <Paper
                  elevation={0}
                  sx={{
                    mb: 2.5,
                    p: 2,
                    borderRadius: 2.5,
                    border: "1px solid",
                    borderColor: `${
                      objectiveCategoryColor[challengeDetails.objectiveType] ||
                      "#1976d2"
                    }30`,
                    bgcolor: `${
                      objectiveCategoryColor[challengeDetails.objectiveType] ||
                      "#1976d2"
                    }08`,
                    borderLeft: `4px solid ${
                      objectiveCategoryColor[challengeDetails.objectiveType] ||
                      "#1976d2"
                    }`,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        bgcolor: `${
                          objectiveCategoryColor[
                            challengeDetails.objectiveType
                          ] || "#1976d2"
                        }15`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <LightbulbOutlinedIcon
                        sx={{
                          fontSize: 22,
                          color:
                            objectiveCategoryColor[
                              challengeDetails.objectiveType
                            ] || "#1976d2",
                        }}
                      />
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        color="text.secondary"
                        sx={{
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          fontSize: 10,
                        }}
                      >
                        Dynamic Example
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight={700}
                        sx={{
                          mt: 0.25,
                          color:
                            objectiveCategoryColor[
                              challengeDetails.objectiveType
                            ] || "#1976d2",
                        }}
                      >
                        {(() => {
                          const configuredTemplate =
                            objectiveTypeTitleMap[
                              challengeDetails.objectiveType
                            ] || "";
                          const values = getObjectiveDynamicValues(
                            challengeDetails.objectiveType,
                            challengeDetails.objectiveTarget,
                            challengeDetails.objectiveFilters,
                          );

                          if (configuredTemplate) {
                            return replaceTemplatePlaceholders(
                              configuredTemplate,
                              values,
                            );
                          }

                          return getFallbackDynamicExample(
                            challengeDetails.objectiveType,
                            challengeDetails.objectiveTarget,
                            challengeDetails.objectiveFilters,
                          );
                        })()}
                      </Typography>
                      {!!objectiveTypeTitleMap[
                        challengeDetails.objectiveType
                      ] && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mt: 0.5 }}
                        >
                          Template:{" "}
                          {
                            objectiveTypeTitleMap[
                              challengeDetails.objectiveType
                            ]
                          }
                        </Typography>
                      )}

                      {!objectiveTypeTitleMap[
                        challengeDetails.objectiveType
                      ] && (
                        <Alert
                          severity="warning"
                          variant="outlined"
                          sx={{ mt: 1, py: 0.25, borderRadius: 1.5 }}
                        >
                          No Objective Type Title configured for this objective
                          type yet. Please create one in Objective Type Titles.
                        </Alert>
                      )}

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        {
                          objectiveTypeDescriptions[
                            challengeDetails.objectiveType
                          ].hint
                        }
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              )}

            {/* Conditional filters */}
            {challengeDetails.objectiveType && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                {renderObjectiveFilters(
                  challengeDetails.objectiveType,
                  challengeDetails.objectiveFilters,
                  onFilterChange,
                  challengeDetails.objectiveTarget,
                )}
              </Box>
            )}
          </SectionCard>
        )}

        {/* ── 2a-combo. Combo Title (COMBO only) ──────────── */}
        {challengeDetails.type === "COMBO" && (
          <SectionCard
            step={2}
            icon={<FlagOutlinedIcon fontSize="small" />}
            title="Combo Title"
            subtitle="Set the main title for this combo challenge"
            accent="#6a1b9a"
            isComplete={!!challengeDetails.comboTitle}
          >
            <Box className={styles.field}>
              <SimpleTextInput
                label="Combo Title"
                name="comboTitle"
                value={challengeDetails.comboTitle}
                onChange={(e: any) => {
                  setChallengeDetails((prev) => ({
                    ...prev,
                    comboTitle: e.target.value,
                    comboTitleTrans: {
                      ...prev.comboTitleTrans,
                      en: e.target.value,
                    },
                  }));
                }}
                button={
                  <TranslationButtons
                    field={"comboTitleTrans"}
                    onClick={(type: string, field: string) =>
                      handleOpenModal(type, field)
                    }
                  />
                }
                placeholder="e.g. Weekend Warrior Challenge"
              />
            </Box>
          </SectionCard>
        )}

        {/* ── 2b. Sub-Challenges (COMBO) ───────────────────── */}
        {challengeDetails.type === "COMBO" && (
          <SectionCard
            step={3}
            icon={<LayersIcon fontSize="small" />}
            title={`Sub-Challenges (${challengeDetails.subChallenges.length})`}
            subtitle="Each sub-challenge has its own objective and reward"
            accent="#6a1b9a"
            isComplete={
              challengeDetails.subChallenges.length > 0 &&
              challengeDetails.subChallenges.every(
                (s) => !!s.objectiveType && (s.objectiveTarget ?? 0) > 0,
              )
            }
          >
            {challengeDetails.subChallenges.length === 0 && (
              <Alert
                severity="warning"
                variant="outlined"
                sx={{ mb: 2, borderRadius: 2 }}
                icon={<InfoOutlinedIcon fontSize="small" />}
              >
                <Typography variant="body2">
                  Add at least one sub-challenge. Players must complete{" "}
                  <strong>every</strong> sub-challenge to earn the combo reward.
                </Typography>
              </Alert>
            )}

            {challengeDetails.subChallenges.map((sub, idx) => (
              <Accordion
                key={idx}
                defaultExpanded
                sx={{
                  mb: 2,
                  border: "1px solid",
                  borderColor:
                    sub.objectiveType && (sub.objectiveTarget ?? 0) > 0
                      ? "#a5d6a740"
                      : "divider",
                  borderRadius: "12px !important",
                  "&:before": { display: "none" },
                  boxShadow: "none",
                  overflow: "hidden",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    background:
                      "linear-gradient(135deg, #f5f0ff 0%, #ede7f6 100%)",
                    borderRadius: "12px 12px 0 0",
                    minHeight: 52,
                    "& .MuiAccordionSummary-content": {
                      alignItems: "center",
                      gap: 1,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #6a1b9a, #9c27b0)",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      fontWeight: 700,
                      flexShrink: 0,
                      boxShadow: "0 2px 6px #6a1b9a40",
                    }}
                  >
                    {idx + 1}
                  </Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ flex: 1 }}
                  >
                    {`Sub-Challenge #${idx + 1}`}
                  </Typography>
                  {sub.objectiveType && (
                    <Chip
                      label={
                        objectiveTypeOptions.find(
                          (o) => o.id === sub.objectiveType,
                        )?.label
                      }
                      size="small"
                      sx={{
                        fontSize: 10,
                        fontWeight: 600,
                        height: 22,
                        bgcolor: `${
                          objectiveCategoryColor[sub.objectiveType] || "#6a1b9a"
                        }15`,
                        color:
                          objectiveCategoryColor[sub.objectiveType] ||
                          "#6a1b9a",
                        border: `1px solid ${
                          objectiveCategoryColor[sub.objectiveType] || "#6a1b9a"
                        }30`,
                      }}
                    />
                  )}
                  {sub.rewardType && rewardTypeInfo[sub.rewardType] && (
                    <Chip
                      label={`${rewardTypeInfo[sub.rewardType].icon} ${
                        rewardTypeOptions.find((r) => r.id === sub.rewardType)
                          ?.label || ""
                      }`}
                      size="small"
                      sx={{
                        fontSize: 10,
                        fontWeight: 600,
                        height: 22,
                        bgcolor: "#fff3e0",
                        color: "#e65100",
                        border: "1px solid #ffe0b2",
                      }}
                    />
                  )}
                  {sub.objectiveType &&
                    (sub.objectiveTarget ?? 0) > 0 &&
                    sub.rewardType && (
                      <CheckCircleIcon
                        sx={{ fontSize: 18, color: "#4caf50", mr: 0.5 }}
                      />
                    )}
                  <IconButton
                    color="error"
                    disabled={isLive}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeSubChallenge(idx);
                    }}
                    size="small"
                    sx={{ mr: 0.5 }}
                  >
                    <RemoveCircleOutlineIcon fontSize="small" />
                  </IconButton>
                </AccordionSummary>

                <AccordionDetails sx={{ pt: 2 }}>
                  {/* Objective + reward fields — locked on live challenges */}
                  <Box sx={isLive ? lockedSx : undefined}>
                    {/* Sub objective fields */}
                    <Box
                      sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}
                    >
                      <Box className={styles.field}>
                        <SimpleSelectInput
                          label="Objective Type"
                          name={`sub_objectiveType_${idx}`}
                          value={sub.objectiveType}
                          options={objectiveTypeOptions}
                          onChange={(e: any) =>
                            onSubChallengeChange(
                              idx,
                              "objectiveType",
                              e.target.value,
                            )
                          }
                        />
                      </Box>
                      {!hasFixedObjectiveTarget(sub.objectiveType) && (
                        <Box className={styles.field}>
                          <SimpleTextInput
                            label="Objective Target"
                            name={`sub_objectiveTarget_${idx}`}
                            type="number"
                            value={sub.objectiveTarget ?? ""}
                            onChange={(e: any) =>
                              onSubChallengeChange(
                                idx,
                                "objectiveTarget",
                                e.target.value,
                              )
                            }
                            placeholder="e.g. 3"
                          />
                        </Box>
                      )}
                    </Box>

                    {hasFixedObjectiveTarget(sub.objectiveType) && (
                      <Box sx={{ width: "100%", mb: 2 }}>
                        <Alert severity="info">
                          Completes on the first action — target is fixed at 1,
                          nothing to set.
                        </Alert>
                      </Box>
                    )}

                    {/* Sub objective hint — enhanced */}
                    {sub.objectiveType &&
                      objectiveTypeDescriptions[sub.objectiveType] && (
                        <Paper
                          elevation={0}
                          sx={{
                            mb: 2,
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: `${
                              objectiveCategoryColor[sub.objectiveType] ||
                              "#6a1b9a"
                            }06`,
                            borderLeft: `3px solid ${
                              objectiveCategoryColor[sub.objectiveType] ||
                              "#6a1b9a"
                            }`,
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <LightbulbOutlinedIcon
                            sx={{
                              fontSize: 18,
                              color:
                                objectiveCategoryColor[sub.objectiveType] ||
                                "#6a1b9a",
                            }}
                          />
                          <Box>
                            {!!objectiveTypeTitleMap[sub.objectiveType] && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", mb: 0.25 }}
                              >
                                Template:{" "}
                                {objectiveTypeTitleMap[sub.objectiveType]}
                              </Typography>
                            )}
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              sx={{
                                color:
                                  objectiveCategoryColor[sub.objectiveType] ||
                                  "#6a1b9a",
                              }}
                            >
                              {(() => {
                                const configuredTemplate =
                                  objectiveTypeTitleMap[sub.objectiveType] ||
                                  "";
                                const values = getObjectiveDynamicValues(
                                  sub.objectiveType,
                                  sub.objectiveTarget,
                                  sub.objectiveFilters,
                                );

                                if (configuredTemplate) {
                                  return replaceTemplatePlaceholders(
                                    configuredTemplate,
                                    values,
                                  );
                                }

                                return getFallbackDynamicExample(
                                  sub.objectiveType,
                                  sub.objectiveTarget,
                                  sub.objectiveFilters,
                                );
                              })()}
                            </Typography>
                            {!objectiveTypeTitleMap[sub.objectiveType] && (
                              <Alert
                                severity="warning"
                                variant="outlined"
                                sx={{ mt: 0.75, py: 0.25, borderRadius: 1.5 }}
                              >
                                No Objective Type Title configured for this
                                objective type yet. Please create one in
                                Objective Type Titles.
                              </Alert>
                            )}
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {
                                objectiveTypeDescriptions[sub.objectiveType]
                                  .hint
                              }
                            </Typography>
                          </Box>
                        </Paper>
                      )}

                    {/* Sub filters */}
                    {sub.objectiveType && (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 2,
                          mb: 2,
                        }}
                      >
                        {renderObjectiveFilters(
                          sub.objectiveType,
                          sub.objectiveFilters,
                          (field, value) =>
                            onSubFilterChange(idx, field, value),
                          sub.objectiveTarget,
                        )}
                      </Box>
                    )}

                    {/* Sub-challenge Tier — Color Chips */}
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontWeight={600}
                        sx={{ mb: 1 }}
                      >
                        Sub-Challenge Tier
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        {challengeTierOptions.map((opt) => {
                          const vis = tierVisuals[opt.id] || tierVisuals.OTHER;
                          const isSelected = sub.tier === opt.id;
                          return (
                            <Chip
                              key={opt.id}
                              label={`${vis.icon}  ${opt.label}`}
                              clickable
                              size="small"
                              onClick={() =>
                                onSubChallengeChange(
                                  idx,
                                  "tier",
                                  isSelected ? "" : opt.id,
                                )
                              }
                              sx={{
                                fontSize: 12,
                                fontWeight: 600,
                                borderRadius: 1.5,
                                border: "2px solid",
                                borderColor: isSelected
                                  ? vis.border
                                  : "#e0e0e0",
                                bgcolor: isSelected ? vis.bg : "white",
                                color: isSelected ? vis.text : "#616161",
                                transition: "all 0.2s",
                                "&:hover": {
                                  borderColor: vis.border,
                                  bgcolor: vis.bg,
                                },
                              }}
                            />
                          );
                        })}
                      </Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 0.5 }}
                      >
                        Each sub-challenge can have its own tier. If left empty,
                        the root tier is used.
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Sub reward */}
                    <Typography
                      component="div"
                      variant="body2"
                      fontWeight={700}
                      color="text.secondary"
                      sx={{
                        mb: 1.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                      }}
                    >
                      <EmojiEventsIcon
                        fontSize="small"
                        sx={{ color: "#f57c00" }}
                      />
                      Sub-Challenge Reward
                      {sub.rewardType && rewardTypeInfo[sub.rewardType] && (
                        <Chip
                          label={rewardTypeInfo[sub.rewardType].icon}
                          size="small"
                          sx={{ fontSize: 14, height: 22, ml: 0.5 }}
                        />
                      )}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 2,
                        alignItems: "flex-start",
                      }}
                    >
                      <Box className={styles.field}>
                        <SimpleSelectInput
                          label="Reward Type"
                          name={`sub_rewardType_${idx}`}
                          value={sub.rewardType}
                          options={rewardTypeOptions}
                          onChange={(e: any) =>
                            onSubChallengeChange(
                              idx,
                              "rewardType",
                              e.target.value,
                            )
                          }
                        />
                      </Box>
                      <Box sx={{ width: 140 }}>
                        <SimpleTextInput
                          label={
                            isPackRewardType(sub.rewardType)
                              ? "Quantity (packs)"
                              : sub.rewardType === "XP_MULTIPLIER"
                                ? "Multiplier"
                                : "Reward Value"
                          }
                          name={`sub_rewardValue_${idx}`}
                          type="number"
                          value={sub.rewardValue ?? ""}
                          disabled={hasFixedRewardQuantity(sub.rewardType)}
                          onChange={(e: any) => {
                            onSubChallengeChange(
                              idx,
                              "rewardValue",
                              e.target.value,
                            );
                          }}
                          placeholder={
                            isPackRewardType(sub.rewardType)
                              ? "e.g. 2"
                              : sub.rewardType === "XP_MULTIPLIER"
                                ? "e.g. 3 for 3x"
                                : "e.g. 50"
                          }
                        />
                      </Box>
                      {sub.rewardType === "XP" && (
                        <Box sx={{ alignSelf: "center", mt: 3 }}>
                          <CustomButton
                            onClick={() =>
                              handleOpenTerritoryXpModal(sub, true, idx)
                            }
                            disabled={isTerritoryXpLoading || !isNaturalNum(sub.rewardValue)}
                          >
                            {isTerritoryXpLoading
                              ? "Loading..."
                              : "Populate XP for All Territories"}
                          </CustomButton>
                        </Box>
                      )}
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 2,
                        alignItems: "flex-start",
                      }}
                    >
                      {renderRewardExtras(sub, (field, value) =>
                        onSubChallengeChange(idx, field, value),
                      )}
                    </Box>
                  </Box>

                  {/* Sub-challenge reward image — editable even on live challenges */}
                  {sub.rewardType && (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        Reward Image
                      </Typography>
                      <FilePicker
                        value={subRewardImages[idx] ?? null}
                        name={`sub_${idx}_rewardImage`}
                        onFileSelect={(img: any, dims?: any) => {
                          setSubRewardImages((prev) => {
                            const next = [...prev];
                            next[idx] = img;
                            return next;
                          });
                          setSubRewardImageDimensions((prev) => {
                            const next = [...prev];
                            next[idx] = dims ?? null;
                            return next;
                          });
                        }}
                        imageType={ImageType.CHALLENGE_REWARD_IMAGE}
                        enableDimensionControl={true}
                        metaData={{
                          note: `Recommended: ${DEFAULT_IMAGE_DIMENSIONS[ImageType.CHALLENGE_REWARD_IMAGE].width}×${DEFAULT_IMAGE_DIMENSIONS[ImageType.CHALLENGE_REWARD_IMAGE].height}px. Resized to WebP.`,
                        }}
                      />
                      {!subRewardImages[idx] &&
                        renderRewardPreview(
                          subRewardPreviews[idx],
                          sub.rewardType,
                          `Sub ${idx + 1} Reward`,
                          150,
                          260,
                        )}
                    </Box>
                  )}

                  {/* Sub-challenge pack selectors — locked on live challenges */}
                  <Box sx={isLive ? lockedSx : undefined}>
                    {isPackRewardType(sub.rewardType) && (
                      <Box sx={{ mt: 2 }}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Select Reward Packs
                        </Typography>
                        <Box className={styles.field}>
                          <SearchableInput
                            label="Reward Packs"
                            name={`sub_${idx}_rewardPacks`}
                            isMultiSelect={true}
                            selected={subPackSelections[idx] ?? []}
                            options={packOptions}
                            maxSelections={
                              Number.isInteger(Number(sub.rewardValue)) &&
                              Number(sub.rewardValue) > 0
                                ? Number(sub.rewardValue)
                                : undefined
                            }
                            onMaxSelectionReached={() => {
                              const allowedCount = Number(sub.rewardValue);
                              showSnackbar(
                                `You can select only ${allowedCount} pack ID${allowedCount > 1 ? "s" : ""} for Sub-challenge #${idx + 1}`,
                                "error",
                              );
                            }}
                            onInputChange={handlePackSearch}
                            onOptionSelect={(e: any) => {
                              const selections = e.target.value || [];
                              const packIds = selections
                                .map((s: any) => s?.id || "")
                                .filter(Boolean);
                              onSubChallengeChange(idx, "rewardPacks", packIds);
                              setSubPackSelections((prev) => {
                                const next = prev.map((row) => [...row]);
                                while (next.length <= idx) next.push([]);
                                next[idx] = selections;
                                return next;
                              });
                            }}
                            placeholder="Search and select packs"
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}

            <Box sx={{ textAlign: "center", pt: 1.5 }}>
              <Button
                startIcon={<AddCircleOutlineIcon />}
                onClick={addSubChallenge}
                disabled={isLive}
                variant="outlined"
                size="small"
                sx={{
                  textTransform: "none",
                  borderStyle: "dashed",
                  borderWidth: 2,
                  borderColor: "#6a1b9a40",
                  color: "#6a1b9a",
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: "#6a1b9a",
                    bgcolor: "#f3e5f5",
                    borderStyle: "dashed",
                  },
                }}
              >
                Add Sub-Challenge
              </Button>
            </Box>
          </SectionCard>
        )}

        {/* ── 3. Reward Configuration ─────────────────────── */}
        <SectionCard
          step={rewardSectionStep}
          icon={<EmojiEventsIcon fontSize="small" />}
          title={
            challengeDetails.type === "COMBO"
              ? "Combo Completion Reward"
              : "Reward Configuration"
          }
          accent="#e65100"
        >
          {challengeDetails.type === "COMBO" && (
            <Alert
              severity="warning"
              variant="outlined"
              sx={{ mb: 2, py: 0.5, borderRadius: 1 }}
            >
              This reward is given{" "}
              <strong>only after all sub-challenges are completed</strong>. Each
              sub-challenge above can also carry its own separate reward.
            </Alert>
          )}

          {/* Combo Completion Reward Title */}
          {challengeDetails.type === "COMBO" && (
            <Box sx={{ mb: 2 }}>
              <Box className={styles.field}>
                <SimpleTextInput
                  label="Reward Title"
                  name="comboRewardTitle"
                  value={challengeDetails.comboRewardTitle}
                  onChange={(e: any) => {
                    setChallengeDetails((prev) => ({
                      ...prev,
                      comboRewardTitle: e.target.value,
                      comboRewardTitleTrans: {
                        ...prev.comboRewardTitleTrans,
                        en: e.target.value,
                      },
                    }));
                  }}
                  button={
                    <TranslationButtons
                      field={"comboRewardTitleTrans"}
                      onClick={(type: string, field: string) =>
                        handleOpenModal(type, field)
                      }
                    />
                  }
                  placeholder="e.g. Ultimate Combo Reward"
                />
              </Box>
            </Box>
          )}
          <Box sx={isLive ? lockedSx : undefined}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "flex-start",
              }}
            >
              <Box className={styles.field}>
                <SimpleSelectInput
                  label="Reward Type"
                  name="rewardType"
                  value={challengeDetails.rewardType}
                  options={rewardTypeOptions}
                  onChange={onChange}
                />
              </Box>
              <Box sx={{ width: 140 }}>
                <SimpleTextInput
                  label={
                    isPackRewardType(challengeDetails.rewardType)
                      ? "Quantity (number of packs)"
                      : challengeDetails.rewardType === "XP_MULTIPLIER"
                        ? "Multiplier"
                        : "Reward Value"
                  }
                  name="rewardValue"
                  type="number"
                  value={challengeDetails.rewardValue ?? ""}
                  disabled={hasFixedRewardQuantity(challengeDetails.rewardType)}
                  onChange={onChange}
                  placeholder={
                    isPackRewardType(challengeDetails.rewardType)
                      ? "e.g. 2"
                      : challengeDetails.rewardType === "XP_MULTIPLIER"
                        ? "e.g. 3 for 3x"
                        : "e.g. 100"
                  }
                />
              </Box>
              {challengeDetails.rewardType === "XP" && (
                <Box sx={{ alignSelf: "center", mt: 3 }}>
                  <CustomButton
                    onClick={() =>
                      handleOpenTerritoryXpModal(challengeDetails, false)
                    }
                    disabled={isTerritoryXpLoading || !isNaturalNum(challengeDetails.rewardValue)}
                  >
                    {isTerritoryXpLoading
                      ? "Loading..."
                      : "Populate XP for All Territories"}
                  </CustomButton>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "flex-start",
              }}
            >
              {renderRewardExtras(challengeDetails, (field, value) =>
                setChallengeDetails((prev) => ({ ...prev, [field]: value })),
              )}
            </Box>
          </Box>

          {/* Reward type info card */}
          {challengeDetails.rewardType &&
            rewardTypeInfo[challengeDetails.rewardType] && (
              <Paper
                elevation={0}
                sx={{
                  mt: 2,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: `${
                    rewardTypeInfo[challengeDetails.rewardType].color
                  }08`,
                  border: `1px solid ${
                    rewardTypeInfo[challengeDetails.rewardType].color
                  }20`,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Typography sx={{ fontSize: 28, lineHeight: 1 }}>
                  {rewardTypeInfo[challengeDetails.rewardType].icon}
                </Typography>
                <Box>
                  <Typography variant="body2" fontWeight={700}>
                    {
                      rewardTypeOptions.find(
                        (r) => r.id === challengeDetails.rewardType,
                      )?.label
                    }
                    {challengeDetails.rewardValue
                      ? ` — ${challengeDetails.rewardValue}`
                      : ""}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {rewardTypeInfo[challengeDetails.rewardType].desc}
                  </Typography>
                </Box>
              </Paper>
            )}

          {/* Reward Image — editable even on live challenges */}
          {challengeDetails.rewardType && (
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="body2"
                fontWeight={600}
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Reward Image
              </Typography>
              <FilePicker
                value={rewardImage ?? null}
                name="rewardImage"
                onFileSelect={(img: any, dims?: any) => {
                  setRewardImage(img);
                  setRewardImageDimensions(dims ?? null);
                }}
                imageType={ImageType.CHALLENGE_REWARD_IMAGE}
                enableDimensionControl={true}
                metaData={{
                  note: `Recommended: ${DEFAULT_IMAGE_DIMENSIONS[ImageType.CHALLENGE_REWARD_IMAGE].width}×${DEFAULT_IMAGE_DIMENSIONS[ImageType.CHALLENGE_REWARD_IMAGE].height}px. Resized to WebP.`,
                }}
              />
              {!rewardImage &&
                renderRewardPreview(
                  rewardPreview,
                  challengeDetails.rewardType,
                  "Reward",
                  170,
                  300,
                )}
            </Box>
          )}

          {/* Pack selectors — locked on live challenges */}
          <Box sx={isLive ? lockedSx : undefined}>
            {isPackRewardType(challengeDetails.rewardType) && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Select Reward Packs
                </Typography>
                <Box className={styles.field}>
                  <SearchableInput
                    label="Reward Packs"
                    name="rewardPacks"
                    isMultiSelect={true}
                    selected={packSelections}
                    options={packOptions}
                    maxSelections={
                      Number.isInteger(Number(challengeDetails.rewardValue)) &&
                      Number(challengeDetails.rewardValue) > 0
                        ? Number(challengeDetails.rewardValue)
                        : undefined
                    }
                    onMaxSelectionReached={() => {
                      const allowedCount = Number(challengeDetails.rewardValue);
                      showSnackbar(
                        `You can select only ${allowedCount} pack ID${allowedCount > 1 ? "s" : ""} for the configured quantity`,
                        "error",
                      );
                    }}
                    onInputChange={handlePackSearch}
                    onOptionSelect={(e: any) => {
                      const selections = e.target.value || [];
                      const packIds = selections
                        .map((s: any) => s?.id || "")
                        .filter(Boolean);
                      setChallengeDetails((prev) => ({
                        ...prev,
                        rewardPacks: packIds,
                      }));
                      setPackSelections(selections);
                    }}
                    placeholder="Search and select packs"
                  />
                </Box>
              </Box>
            )}
          </Box>
        </SectionCard>

        {/* ── 4. Schedule ─────────────────────────────────── */}
        <SectionCard
          step={scheduleSectionStep}
          icon={<CalendarMonthIcon fontSize="small" />}
          title="Schedule"
          subtitle="When and how often the challenge runs"
          accent="#2e7d32"
          locked={isLive}
          isComplete={
            !!challengeDetails.startDate && !!challengeDetails.endDate
          }
        >
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
            <Box className={styles.field}>
              <SimpleTimeDateInput
                label="Start Date"
                name="startDate"
                value={challengeDetails.startDate || null}
                format="dd/MM/yyyy HH:mm:ss"
                minutesStep={1}
                timeSteps={{ hours: 1, minutes: 1, seconds: 1 }}
                views={["year", "month", "day", "hours", "minutes", "seconds"]}
                readOnly={false}
                removeBottomMargin={true}
                // The start can only be in the future — past dates/times are disabled.
                disablePast
                minDateTime={new Date()}
                onChange={(value: any) => onDateChange("startDate", value)}
              />
            </Box>
            <Box className={styles.field}>
              <SimpleTimeDateInput
                label="End Date"
                name="endDate"
                value={challengeDetails.endDate || null}
                format="dd/MM/yyyy HH:mm:ss"
                minutesStep={1}
                timeSteps={{ hours: 1, minutes: 1, seconds: 1 }}
                views={["year", "month", "day", "hours", "minutes", "seconds"]}
                readOnly={false}
                removeBottomMargin={true}
                // The end must be after the start (and in the future).
                disablePast
                minDateTime={
                  challengeDetails.startDate
                    ? new Date(challengeDetails.startDate)
                    : new Date()
                }
                onChange={(value: any) => onDateChange("endDate", value)}
              />
            </Box>
          </Box>

          {/* Schedule Type — Visual Cards */}
          <Typography
            variant="body2"
            color="text.secondary"
            fontWeight={600}
            sx={{ mb: 1.5 }}
          >
            Schedule Type
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 1 }}>
            {scheduleTypeOptions.map((opt) => {
              const info = scheduleInfo[opt.id];
              const isSelected = challengeDetails.scheduleType === opt.id;
              return (
                <Paper
                  key={opt.id}
                  elevation={0}
                  onClick={() =>
                    onChange({
                      target: {
                        name: "scheduleType",
                        value: opt.id,
                      },
                    } as any)
                  }
                  sx={{
                    px: 2,
                    py: 1.5,
                    cursor: "pointer",
                    borderRadius: 2,
                    border: "2px solid",
                    borderColor: isSelected ? "#2e7d32" : "#e0e0e0",
                    bgcolor: isSelected ? "#e8f5e9" : "white",
                    transition: "all 0.2s",
                    minWidth: 100,
                    textAlign: "center",
                    "&:hover": {
                      borderColor: "#2e7d32",
                      bgcolor: "#e8f5e980",
                    },
                  }}
                >
                  <Typography sx={{ fontSize: 20, mb: 0.25 }}>
                    {info?.icon || "📋"}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={isSelected ? 700 : 500}
                  >
                    {opt.label}
                  </Typography>
                </Paper>
              );
            })}
          </Box>

          {/* Schedule description */}
          {challengeDetails.scheduleType &&
            scheduleInfo[challengeDetails.scheduleType] && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1 }}
              >
                {scheduleInfo[challengeDetails.scheduleType].desc}
              </Typography>
            )}

          {/* repeatEveryDays — required when CUSTOM is selected */}
          {challengeDetails.scheduleType === "CUSTOM" && (
            <Box
              sx={{
                mt: 2,
                width: "fit-content",
                minWidth: 220,
                maxWidth: "100%",
                p: 2,
                borderRadius: 2,
                border: "2px solid #2e7d32",
                bgcolor: "#e8f5e9",
                boxSizing: "border-box",
              }}
            >
              <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                ⚙️ Cycle Interval (days){" "}
                <Typography component="span" variant="caption" color="error">
                  *
                </Typography>
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1 }}
              >
                How many days each cycle lasts. e.g. 3 means the challenge
                resets every 3 days.
              </Typography>
              <input
                type="number"
                min={1}
                placeholder="e.g. 3"
                value={challengeDetails.repeatEveryDays ?? ""}
                onChange={(e) => {
                  const val = e.target.value;
                  setChallengeDetails((prev) => ({
                    ...prev,
                    repeatEveryDays: val ? Number(val) : null,
                  }));
                }}
                style={{
                  width: "100%",
                  maxWidth: "100%",
                  boxSizing: "border-box",
                  padding: "8px 12px",
                  borderRadius: 8,
                  border: `2px solid ${
                    !challengeDetails.repeatEveryDays ? "#e53935" : "#2e7d32"
                  }`,
                  fontSize: 14,
                  outline: "none",
                  background: "white",
                }}
              />
              {!challengeDetails.repeatEveryDays && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ display: "block", mt: 0.5 }}
                >
                  Please enter a cycle interval to continue.
                </Typography>
              )}
            </Box>
          )}
        </SectionCard>

        {/* ── 5. Targeting & Settings ───────────────────────── */}
        <SectionCard
          step={settingsSectionStep}
          icon={<TuneIcon fontSize="small" />}
          title="Targeting & Settings"
          subtitle="Control who sees this challenge and how it behaves"
          accent="#37474f"
        >
          {/* Territories — evaluated only when operators[] is empty */}
          <Box sx={{ width: "100%", mb: 1 }}>
            <SearchableInput
              label="Territories (leave empty for all; ignored when operators are set)"
              name="territories"
              isMultiSelect={true}
              enableSelectAll={true}
              selected={(challengeDetails.territories || []).map(
                (code: string) => {
                  const opt = (sessionHelperInstance.countryOptions || []).find(
                    (o: any) => o.id === code,
                  );
                  return opt || { id: code, label: code };
                },
              )}
              options={(sessionHelperInstance.countryOptions || []).filter(
                (o: any) => o.id !== "all",
              )}
              onOptionSelect={(e: any) => {
                const selections = e.target.value || [];
                const codes = selections
                  .map((s: any) => s?.id || s)
                  .filter(Boolean);
                setChallengeDetails((prev) => ({
                  ...prev,
                  territories: codes,
                }));
              }}
              placeholder="Search countries"
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              alignItems: "flex-start",
            }}
          >
            <Box sx={{ minWidth: 280, flex: "1 1 280px" }}>
              <SearchableInput
                label="Operators (leave empty for all)"
                name="operators"
                isMultiSelect={true}
                enableSelectAll={true}
                selected={(challengeDetails.operators || []).map(
                  (opId: any) => {
                    const opt = (
                      sessionHelperInstance.operatorOptions || []
                    ).find((o: any) => o.id === opId);
                    return opt || { id: opId, label: opId };
                  },
                )}
                options={(sessionHelperInstance.operatorOptions || []).filter(
                  (o: any) =>
                    !(challengeDetails.excludedOperators || []).includes(o.id),
                )}
                onOptionSelect={(e: any) => {
                  const selections = e.target.value || [];
                  const newIds = selections
                    .map((s: any) => s?.id || s)
                    .filter(Boolean);
                  setChallengeDetails((prev) => ({
                    ...prev,
                    operators: newIds,
                    excludedOperators: (prev.excludedOperators || []).filter(
                      (id: any) => !newIds.includes(id),
                    ),
                  }));
                }}
                placeholder="Search operators"
              />
            </Box>
            <Box sx={{ minWidth: 280, flex: "1 1 280px" }}>
              <SearchableInput
                label="Excluded Operators"
                name="excludedOperators"
                isMultiSelect={true}
                enableSelectAll={true}
                selected={(challengeDetails.excludedOperators || []).map(
                  (opId: any) => {
                    const opt = (
                      sessionHelperInstance.operatorOptions || []
                    ).find((o: any) => o.id === opId);
                    return opt || { id: opId, label: opId };
                  },
                )}
                options={(sessionHelperInstance.operatorOptions || []).filter(
                  (o: any) =>
                    !(challengeDetails.operators || []).includes(o.id),
                )}
                onOptionSelect={(e: any) => {
                  const selections = e.target.value || [];
                  const newIds = selections
                    .map((s: any) => s?.id || s)
                    .filter(Boolean);
                  setChallengeDetails((prev) => ({
                    ...prev,
                    excludedOperators: newIds,
                    operators: (prev.operators || []).filter(
                      (id: any) => !newIds.includes(id),
                    ),
                  }));
                }}
                placeholder="Search operators to exclude"
              />
            </Box>
            {/* <Box className={styles.field}>
              <SimpleTextInput
                label="Sort Order"
                name="sortOrder"
                type="number"
                value={challengeDetails.sortOrder ?? ""}
                onChange={onChange}
                placeholder="e.g. 1"
              />
            </Box> */}
            <Box sx={{ alignSelf: "center" }}>
                <SimpleSwitch
                  legendText="Challenge Status"
                  name="isActive"
                  value={challengeDetails.isActive}
                  label={challengeDetails.isActive ? "Active" : "Inactive"}
                  onChange={onChange}
                />
            </Box>
            <Box sx={{ alignSelf: "center" }}>
                <SimpleSwitch
                  legendText="Claimable During FTUE"
                  name="isFtueChallenge"
                  value={!!challengeDetails.isFtueChallenge}
                  label={challengeDetails.isFtueChallenge ? "Yes" : "No"}
                  onChange={onChange}
                />
            </Box>
          </Box>
        </SectionCard>

        {/* ── Bulk Translation Upload (COMBO only) ── */}
        {isEdit && challengeDetails.type === "COMBO" && (
          <SectionCard
            step={settingsSectionStep + 1}
            icon={<TranslateIcon fontSize="small" />}
            title="Bulk Translation Upload"
            subtitle="Upload translations for combo titles in bulk via Excel"
            accent="#00838f"
          >
            <Box
              sx={{
                "& .MuiCard-root": {
                  boxShadow: "none",
                  border: "none",
                  p: 0,
                  borderRadius: 0,
                  "&:hover": { boxShadow: "none" },
                },
              }}
            >
              <BulkTranslationUpload
                assetType="CHALLENGE"
                assetId={id}
                fields={[
                  { label: "Combo Title", fieldName: "comboTitleTrans" },
                  {
                    label: "Combo Reward Title",
                    fieldName: "comboRewardTitleTrans",
                  },
                ]}
                onSuccess={fetchChallengeInfo}
              />
            </Box>
          </SectionCard>
        )}
        {!isEdit && challengeDetails.type === "COMBO" && (
          <SectionCard
            step={settingsSectionStep + 1}
            icon={<TranslateIcon fontSize="small" />}
            title="Bulk Translation Upload"
            subtitle="Upload translations for combo titles in bulk via Excel"
            accent="#00838f"
          >
            <Box
              sx={{
                "& .MuiCard-root": {
                  boxShadow: "none",
                  border: "none",
                  p: 0,
                  borderRadius: 0,
                  "&:hover": { boxShadow: "none" },
                },
              }}
            >
              <BulkTranslationUpload
                assetType="CHALLENGE"
                fields={[
                  { label: "Combo Title", fieldName: "comboTitleTrans" },
                  {
                    label: "Combo Reward Title",
                    fieldName: "comboRewardTitleTrans",
                  },
                ]}
                deferUpload
                onFileChange={setTranslationFile}
              />
            </Box>
          </SectionCard>
        )}
      </Box>

      {/* ── Translation Modal ── */}
      <ModalContainer
        openModel={isTerritoryXpModalOpen}
        handleClose={handleCloseTerritoryXpModal}
        overflow="hidden"
      >
        <ModalLayout
          isLargeContainer={false}
          hasPadding={true}
          overflow="hidden"
        >
          <Box
            sx={{
              minWidth: { xs: 280, sm: 420 },
              maxWidth: 720,
              maxHeight: "80vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ flexShrink: 0 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Populate XP for All Territories
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Review and update the territory-wise XP values below.
              </Typography>
            </Box>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "minmax(140px, 1fr) minmax(180px, 1fr)",
                  gap: 1,
                  pb: 1,
                  mb: 1,
                  borderBottom: "1px solid #e0e0e0",
                  fontWeight: 700,
                }}
              >
                <Typography>Country Code</Typography>
                <Typography>Value</Typography>
              </Box>

              {Object.entries(currentTerritoryXpModalValues).length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No territory XP values available yet.
                </Typography>
              ) : (
                <TerritoryXpVirtualList
                  territoryEntries={territoryEntries}
                  territoryXpErrors={territoryXpErrors}
                  handleTerritoryXpValueChange={handleTerritoryXpValueChange}
                />
              )}
            </Box>

            <Box
              sx={{
                flexShrink: 0,
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mt: 2,
              }}
            >
              <Button variant="outlined" onClick={handleCloseTerritoryXpModal}>
                Cancel
              </Button>
              <Button variant="contained" onClick={()=>handleSaveTerritoryXpValues()}>
                Save
              </Button>
            </Box>
          </Box>
        </ModalLayout>
      </ModalContainer>

      <ViewLanguageModal
        isMultiline={true}
        label="Select Language"
        isOpen={isOpen}
        close={close}
        onChange={handleModalChange}
        handleOnSubmit={handleAddTranslation}
        actionHandlers={{
          handleLangEditClick: handleLangEditClick,
          handleLangDeleteClick: handleLangDeleteClick,
        }}
        modalData={modalData}
        tableColumns={[
          {
            id: "lang",
            label: "Language",
            alignHead: "center",
            alignBody: "center",
          },
          {
            id: "inputValue",
            label: "Translation",
            alignBody: "center",
            alignHead: "center",
          },
          {
            id: "actions",
            label: "Actions",
            alignHead: "center",
            alignBody: "center",
          },
        ]}
      />
    </ContentShell>
  );
};

export default ChallengesEditor;
